import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { STATUS } from '../../../models/status';
import { AppService } from '../../../services/app.service';
import { TransactionsService } from '../../../services/transactions.service';
import { UtilsService } from '../../../services/utils.service';
import { AuthService } from '../../../services/auth.service';
import { Roles } from '../../../models/roles';
import { UserService } from '../../../services/users.service';
import { LoggerService } from '../../../services/logger.service';
import { COUNTRY_OPTIONS } from '../../../models/countries';
import { IKushki, init, KushkiError, KushkiOptions } from '@kushki/js-sdk';
import {
  CardOptions,
  ICard,
  initCardToken,
  TokenResponse,
} from '@kushki/js-sdk/Card';
import { ProvidersCashinService } from '../../../services/cashin.service';
import { ProviderServiceService } from '../../../services/provider_service.service';
import { NotbankService } from '../../../services/notbank.service';
import {
  EffectiveFeeRow,
  FeesService,
} from '../../../services/fees.service';
import {
  NOTBANK_KIND_OPTIONS,
  NOTBANK_PIX_TYPE_OPTIONS,
  countryForNotbankCurrency,
  unwrapNotbankList,
} from '../add-cashout/currencies/notbank-cashout.logic';
import {
  extractNotbankErrorBody,
  isNotbankAccountProvisioned,
  isNotbankProvisionRequiredError,
} from '../notbank-provision/notbank-provision.logic';
import { getSubwalletsForCurrency, normalizeCurrencyCode } from '../../../utils/merge-balance';
import {
  extractPayinLinkError,
  parsePayinCreatedView,
  PayinDepositInstructions,
} from '../../../utils/payin-link-result';
import { BitgoService } from '../../../services/bitgo.service';
import { Subscription } from 'rxjs';
import { VZLA_BANKS } from '../../../models/vzla-banks';
import {
  buildCryptoDeclarationMetadata,
  flattenBitgoAddresses,
  isCryptoDeclareCurrency,
  parseProviderServiceId,
  resolveDeclaredCryptoDeposit,
} from './currencies/declared-crypto-cashin.logic';

interface DepaCardFeePreview {
  currency: string;
  gross: number;
  fee: number;
  net: number;
  percentage: number;
  flat: number;
}

interface PayinCardCharge {
  currency: string;
  amount: number;
}

@Component({
  selector: 'app-add-cashin',
  templateUrl: './add-cashin.component.html',
  styleUrl: './add-cashin.component.scss',
})
export class AddCashinComponent implements OnInit, OnDestroy {
  @Input() immediateDebitOnly = false;
  private readonly DECLARATION_OPTION_ID = '__declare_cashin__';
  private readonly DEPA_IBAN_OPTION_ID = '__depa_iban__';
  private readonly DEPA_CARD_OPTION_ID = '__depa_card__';
  private readonly CBPAY_USDT_WALLET_ID = 'CBPAY_TRC20';
  private static PAYIN_USERNAME_MATCH = ['clearjunction', 'minteo', 'bitso', 'depa', 'notbank'];
  private static readonly DEPA_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD'];
  private static readonly MINTEO_COP_PROVIDER_SERVICE_ID = 39;
  private static readonly CLEARJUNCTION_EUR_PROVIDER_SERVICE_ID = 33;
  private static readonly CBPAY_PROVIDER_ID = 10;
  private static readonly CBPAY_USDT_PROVIDER_SERVICE_ID = 71;

  @Input() currency: string = '';
  /** When true, only show currencies/providers that create a payment link/QR. */
  @Input() paymentLinkOnly = false;
  currencyPicker: any = null;
  countryOptions = COUNTRY_OPTIONS;

  currencyType: any;

  roles = Roles;

  showNewCashin = false;
  showNewCashinType = true;
  newCashinType: null | string = null;
  processing = false;
  providers: any = null;
  selectedProvider: any = null;
  transactionTypes: Array<{ currency: string; id: string }> = [];
  declarationOption = {
    id: this.DECLARATION_OPTION_ID,
    metadata: {
      description: 'Declarar cashin',
      disclaimer: 'Registrar manualmente un cashin ya recibido.',
    },
  };
  depaIbanOption = {
    id: this.DEPA_IBAN_OPTION_ID,
    metadata: {
      description: 'Transferencia IBAN',
      disclaimer:
        'Solo muestra el IBAN. No crea cashin ahora: al llegar la transferencia se crea y se asigna solo a la wallet del cliente.',
      provider_slug: 'depa_iban',
    },
  };
  depaCardOption: any = {
    id: this.DEPA_CARD_OPTION_ID,
    metadata: {
      description: 'Pago con tarjeta',
      disclaimer: 'Checkout seguro para tarjetas.',
      provider_slug: 'depa_card',
    },
  };
  newCashinForm = this.formBuilder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    documentType: ['', Validators.required],
    documentNumber: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: [null, Validators.required],
    address: ['', Validators.required],
    city: ['', Validators.required],
    region: ['', Validators.required],
    country: ['', Validators.required],
    zipcode: ['', Validators.required],
  });

  declarationCashinForm = this.formBuilder.group({
    wallet: [null as { id: string; shortname?: string; name?: string } | null, Validators.required],
    client_id: [
      !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
      Validators.required,
    ],
    reference: [''],
    commission: [0],
    firstname: [''],
    lastname: [''],
    id_type: ['V'],
    id_number: [''],
    transaction_type: [null as string | null],
    company_id: [null as any],
    account_type: [''],
    account_number: [''],
    account_id: [''],
    purpose: ['salaries'],
    external_reference: [''],
    beneficiary_type: ['individual'],
    rail: ['iban'],
    wire_account: [''],
    wire_routing: [''],
    iban: [''],
    bank_bic: [''],
    address_country_code: [''],
    address_line_1: [''],
    protocol: ['clabe'],
    clabe: [''],
    institution_code: [''],
    rfc: [''],
    numeric_ref: [''],
    notes_ref: [''],
  });

  vzlaBanks = VZLA_BANKS;
  r4DebitStep: 'data' | 'otp' = 'data';
  r4CashinId: number | null = null;
  r4DebitLoading = false;
  r4DebitForm = this.formBuilder.group({
    client_id: [
      !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
      Validators.required,
    ],
    bank: [null as { code: string; name: string } | null, Validators.required],
    id_type: ['V', Validators.required],
    id_number: ['', Validators.required],
    telefono: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
    nombre: ['', [Validators.required, Validators.maxLength(20)]],
    concepto: ['', [Validators.required, Validators.maxLength(30)]],
    otp: [''],
  });

  payinLinkCashinForm = this.formBuilder.group({
    client_id: [
      !this.auth.hasRole(Roles.Admin) ? this.auth.user.user_id : null,
      Validators.required,
    ],
    wallet: [null as { id: string; shortname?: string } | null],
    payer_name: [''],
    payer_first_name: [''],
    payer_last_name: [''],
    payer_email: [''],
    payer_phone: [''],
    payer_address: [''],
    payer_postal_code: [''],
    payer_city: [''],
    payer_country: [''],
    payer_country_code: [''],
    payer_state: [''],
    pse_bank: [null as string | null],
    third_party_full_name: [''],
    third_party_legal_identifier_type: ['CC'],
    third_party_legal_identifier: [''],
    third_party_email: [''],
    third_party_phone_number: [''],
    notbank_bank: [null as any],
    notbank_kind: [''],
    notbank_number: [''],
    notbank_pix_type: [''],
    notbank_agency: [''],
    notbank_dv: [''],
    payment_method: [''],
  });

  /* KUSHKI */

  readonly PAYIN_RETURN_URL =
    typeof window !== 'undefined' ? `${window.location.origin}/pay/cop` : '';

  private static readonly BITSO_PENDING_PAYIN_STORAGE_KEY = 'bitso_pending_payin_id';

  private bitsoPayinReturnUrl(): string {
    return typeof window !== 'undefined'
      ? `${window.location.origin}/cashins?bitso_return=1`
      : '';
  }

  private depaPayinReturnUrl(): string {
    return typeof window !== 'undefined'
      ? `${window.location.origin}/transactions?depa_return=1`
      : '';
  }

  private resolvePayinLinkFinalUrl(): string | undefined {
    if (this.isSelectedBitsoCoProvider || this.isSelectedBitsoMxProvider) {
      return this.bitsoPayinReturnUrl() || undefined;
    }
    if (this.isSelectedDepaChannel) {
      return this.depaPayinReturnUrl() || undefined;
    }
    if (this.isSelectedCbpayProvider) {
      if (typeof window === 'undefined' || window.location.protocol !== 'https:') {
        return undefined;
      }
      return `${window.location.origin}/transactions`;
    }
    return this.PAYIN_RETURN_URL || undefined;
  }

  readonly PSE_BANKS_CO: { code: string; label: string }[] = [
    { code: 'BANCO_FALABELLA', label: 'Banco Falabella' },
    { code: 'BANCOLOMBIA', label: 'Bancolombia' },
    { code: 'BANCO_DE_BOGOTA', label: 'Banco de Bogotá' },
    { code: 'BANCO_DAVIVIENDA', label: 'Davivienda' },
    { code: 'BANCO_BBVA', label: 'BBVA Colombia' },
    { code: 'BANCO_AV_VILLAS', label: 'AV Villas' },
    { code: 'BANCO_POPULAR', label: 'Banco Popular' },
    { code: 'BANCO_PICHINCHA', label: 'Banco Pichincha' },
    { code: 'BANCO_SANTANDER', label: 'Santander' },
    { code: 'SCOTIABANK_COLPATRIA', label: 'Scotiabank Colpatria' },
    { code: 'BANCO_AGRARIO', label: 'Banco Agrario' },
    { code: 'BANCO_DE_OCCIDENTE', label: 'Banco de Occidente' },
    { code: 'BANCO_GNB_SUDAMERIS', label: 'GNB Sudameris' },
    { code: 'BANCO_CAJA_SOCIAL', label: 'Caja Social' },
    { code: 'NEQUI', label: 'Nequi' },
    { code: 'DAVIPLATA', label: 'Daviplata' },
    { code: 'BANCO_W', label: 'Banco W' },
    { code: 'BANCO_UNION', label: 'Banco Unión' },
    { code: 'CITIBANK', label: 'Citibank Colombia' },
    { code: 'ITAU', label: 'Itaú Colombia' },
  ];

  readonly LEGAL_IDENTIFIER_TYPES = [
    { value: 'CC', label: 'Cédula de ciudadanía' },
    { value: 'CE', label: 'Cédula de extranjería' },
    { value: 'NIT', label: 'NIT' },
    { value: 'PP', label: 'Pasaporte' },
  ];

  payinCreatedAppLink: string | null = null;
  payinCreatedQrValue: string | null = null;
  payinCreatedQrDataUrl: string | null = null;
  payinCreatedDepositInstructions: PayinDepositInstructions | null = null;
  payinCreatedManualValidation = false;
  payinCreatedProviderError = '';
  payinCreatedMessage = '';
  payinCreatedTransactionId: string | number | null = null;
  payinCreatedFee: DepaCardFeePreview | null = null;
  payinCreatedCardCharge: PayinCardCharge | null = null;
  showPayinLinkCreatedModal = false;
  showDepaCardCheckout = false;
  depaCardWidgetUrl: SafeResourceUrl | null = null;
  depaCardCheckoutStatus = '';
  depaCardCheckoutFee: DepaCardFeePreview | null = null;
  depaCardCheckoutCompleted = false;
  depaCardFeePreview: DepaCardFeePreview | null = null;
  depaCardFeeLoading = false;
  depaCardFeeError = '';
  cbpayFeePreview: DepaCardFeePreview | null = null;
  cbpayFeeLoading = false;
  cbpayFeeError = '';
  private depaCardWidgetOrigin = '';
  private depaCardFeeRequestId = 0;
  private cbpayFeeRequestId = 0;
  private cbpayIdempotencyKey = '';
  depaDepositInstructions: any = null;
  depaDepositLoading = false;
  depaDepositUnavailable = false;
  showDepaAccountRequest = false;
  selectedFile: File | null = null;
  uploadingCSV = false;
  kushkiOptions_PEN: KushkiOptions = {
    publicCredentialId: '866a8f512e6447e3ac35bbfc3b05c6b3',
    inTest: true,
  };

  kushkiOptions_USD: KushkiOptions = {
    publicCredentialId: '12d257cf5ce741e78923d1d85579269a',
    inTest: true,
  };

  amount: any;
  kushkiInstance: IKushki | null = null;
  cardInstance: ICard | null = null;
  kushkiCardOptions: CardOptions | null = null;
  /* KUSHKI */

  showCurrency = false;
  /** Contexto compartido para componentes por moneda sin duplicar los forms actuales. */
  readonly cashinContext = this;

  notbankBanks: any[] = [];
  loadingNotbankBanks = false;
  readonly notbankPixTypeOptions = NOTBANK_PIX_TYPE_OPTIONS;

  showNotbankProvision = false;
  notbankProvisionClientId: number | string | null = null;
  notbankProvisionMissing: string[] = [];
  notbankProvisionPartial: Record<string, unknown> | null = null;
  notbankProvisionBank: Record<string, unknown> | null = null;
  notbankProvisionError = '';
  private pendingPayinAfterProvision: any = null;
  declaredCryptoAddressLoading = false;
  private declaredBitgoAddresses: Array<{ address: string; network?: string }> =
    [];
  private declaredBitgoSub?: Subscription;

  constructor(
    private transactionsService: TransactionsService,
    private cashinService: ProvidersCashinService,
    private formBuilder: FormBuilder,
    public utilsService: UtilsService,
    private messageService: MessageService,
    public auth: AuthService,
    public appService: AppService,
    public userService: UserService,
    private providerServicesService: ProviderServiceService,
    private notbankService: NotbankService,
    private feesService: FeesService,
    private sanitizer: DomSanitizer,
    private bitgoService: BitgoService,
  ) {}

  ngOnInit() {
    if (this.immediateDebitOnly) {
      this.openImmediateDebitForm();
      return;
    }
    if (!this.currency) {
      this.showCurrency = true;
    } else if (!this.isCashinCurrencyAllowed(this.currency)) {
      this.currency = '';
      this.currencyPicker = null;
      this.showCurrency = true;
    } else {
      this.currencyType = this.utilsService.getCurrencyKind(
        this.currency,
        this.appService.config?.currencies,
      );

      this.currencyPicker =
        this.userService.user_balance_config.currencies.find(
          (el: any) => el.id === this.currency,
        );

      this.setCashinTypes();
    }
  }

  setCashinTypes() {
    this.transactionTypes = this.getTransactionTypesForCurrency(this.currency);

    const providerServices =
      this.providerServicesService.providerServices.filter(
        (el: any) =>
          el.origin_coin_id === this.currency &&
          el.service_id == 'cashin' &&
          el.active &&
          (!this.paymentLinkOnly || this.isPayinLinkProviderService(el)),
      );

    const depaProvider = providerServices.find((el: any) =>
      this.isDepaFiatProvider(el),
    );
    const depaCardExtras =
      depaProvider &&
      AddCashinComponent.DEPA_FIAT_CURRENCIES.includes(this.currency)
        ? [this.buildDepaCardOption(depaProvider)]
        : [];

    if (this.paymentLinkOnly) {
      this.providers = [...providerServices, ...depaCardExtras];
      if (this.providers.length) {
        this.setCashinType(this.providers[0]);
      } else {
        this.selectedProvider = null;
        this.newCashinType = null;
      }
      return;
    }

    const hasDepaProvider = providerServices.some((el: any) =>
      this.isDepaFiatProvider(el),
    );
    const depaIbanExtras =
      hasDepaProvider &&
      AddCashinComponent.DEPA_FIAT_CURRENCIES.includes(this.currency)
        ? [this.depaIbanOption]
        : [];

    this.providers = [
      this.declarationOption,
      ...providerServices,
      ...depaCardExtras,
      ...depaIbanExtras,
    ];

    if (
      !this.selectedProvider ||
      this.selectedProvider.id === this.DECLARATION_OPTION_ID
    ) {
      this.setCashinType(this.declarationOption);
    }
  }

  private buildDepaCardOption(providerService: any): any {
    this.depaCardOption = {
      ...providerService,
      id: this.DEPA_CARD_OPTION_ID,
      provider_service_id: providerService.id,
      metadata: {
        ...(providerService.metadata || {}),
        description: 'Pago con tarjeta',
        disclaimer: 'Checkout seguro para tarjetas.',
        provider_slug: 'depa_card',
        image: undefined,
        disclaimer_image: undefined,
      },
    };
    return this.depaCardOption;
  }

  ngOnDestroy(): void {
    this.declaredBitgoSub?.unsubscribe();
  }

  selectCurrency(currency: any) {
    const selected = currency?.value ?? currency;
    this.currency = this.extractCurrencyId(selected);

    if (!this.isCashinCurrencyAllowed(this.currency)) {
      this.currency = '';
      this.currencyPicker = null;
      this.providers = [];
      this.selectedProvider = null;
      this.newCashinType = null;
      return;
    }

    this.currencyPicker = selected;

    this.currencyType = this.utilsService.getCurrencyKind(
      this.currency,
      this.appService.config?.currencies,
    );

    this.setCashinTypes();
    this.declarationCashinForm.controls.wallet.setValue(null);
    this.declarationCashinForm.controls.commission.setValue(0);
    this.resetPayinLinkForm();
    if (!this.paymentLinkOnly) {
      this.setCashinType(this.declarationOption);
    } else if (this.isSelectedDepaCard || this.isSelectedCbpayProvider) {
      this.loadSelectedPayinFeePreview();
    }
  }

  resetForm() {}

  processCashin() {}

  exitNewCashin = () => {
    this.showNewCashinType = false;
    this.showNewCashin = false;
    this.appService.triggerAddCashin(false);
  };

  setCashinType = (provider: any) => {
    this.selectedProvider = provider;
    this.resetPayinLinkForm();

    if (provider?.id === this.DECLARATION_OPTION_ID) {
      this.newCashinType = 'DECLARATION';
      return;
    }

    if (provider?.id === this.DEPA_IBAN_OPTION_ID) {
      this.newCashinType = 'DEPA_IBAN';
      return;
    }

    if (provider?.id === this.DEPA_CARD_OPTION_ID) {
      this.newCashinType = 'DEPA_CARD';
      this.loadDepaCardFeePreview();
      return;
    }

    this.newCashinType = provider?.metadata?.blockchain
      ? provider.metadata.blockchain
      : provider?.provider.username;
    if (this.isSelectedCbpayProvider) {
      this.loadCbpayFeePreview();
    }
  };

  getSelected(provider: any) {
    return this.selectedProvider == provider;
  }

  get isR4ImmediateDebit(): boolean {
    return this.immediateDebitOnly;
  }

  private openImmediateDebitForm() {
    this.currency = 'VES';
    this.showCurrency = false;
    this.showNewCashinType = false;
    this.currencyPicker =
      this.userService.user_balance_config?.currencies?.find(
        (el: any) => el.id === 'VES',
      ) || { id: 'VES', shortname: 'VES' };
    this.setCashinTypes();
    const r4Provider = (this.providers || []).find((provider: any) =>
      this.isR4CashinProvider(provider),
    );
    this.selectedProvider = r4Provider || null;
    this.newCashinType = 'R4';
    this.r4DebitStep = 'data';
    this.r4CashinId = null;
    this.r4DebitForm.enable({ emitEvent: false });
    this.showNewCashin = true;
  }

  private isR4CashinProvider(provider: any): boolean {
    const username = String(provider?.provider?.username || '')
      .trim()
      .toUpperCase();
    const providerId = Number(
      provider?.provider?.user_id ?? provider?.provider?.id ?? provider?.provider_id,
    );
    return username === 'R4' || providerId === 15;
  }

  setR4DocuType(type: string) {
    if (this.r4DebitStep === 'otp') {
      return;
    }
    this.r4DebitForm.controls.id_type.setValue(type);
  }

  requestR4DebitOtp() {
    if (this.r4DebitLoading) {
      return;
    }
    this.r4DebitForm.markAllAsTouched();
    if (!this.selectedProvider) {
      this.setCashinTypes();
      this.selectedProvider =
        (this.providers || []).find((provider: any) =>
          this.isR4CashinProvider(provider),
        ) || null;
    }
    const raw = this.r4DebitForm.getRawValue();
    const clientValue = raw.client_id as any;
    const clientId =
      typeof clientValue === 'object' && clientValue
        ? clientValue.user_id ?? clientValue.id
        : clientValue;
    const bankCode = raw.bank?.code;
    const debitAmount = Number(this.amount);
    if (
      this.r4DebitForm.invalid ||
      !clientId ||
      !bankCode ||
      !this.selectedProvider ||
      !Number.isFinite(debitAmount) ||
      debitAmount <= 0
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos incompletos',
        detail:
          'Completa monto, banco, documento, teléfono, nombre y concepto.',
      });
      return;
    }

    const providerServiceId =
      this.selectedProvider.provider_service_id ?? this.selectedProvider.id;
    this.r4DebitLoading = true;
    this.transactionsService
      .r4ImmediateDebitOtp({
        provider_service_id: providerServiceId,
        client_id: clientId,
        destiny_wallet_id: '0169',
        amount: debitAmount,
        banco: bankCode,
        telefono: String(raw.telefono || ''),
        id_type: raw.id_type || 'V',
        id_number: String(raw.id_number || ''),
        nombre: String(raw.nombre || '').trim(),
        concepto: String(raw.concepto || '').trim(),
      })
      .subscribe({
        next: (resp) => {
          this.r4DebitLoading = false;
          if (resp?.success === false) {
            this.messageService.add({
              severity: 'error',
              summary: 'R4',
              detail: resp.message || 'No se pudo solicitar el OTP.',
            });
            return;
          }
          this.r4CashinId = resp?.data?.id ?? null;
          this.r4DebitStep = 'otp';
          this.r4DebitForm.disable({ emitEvent: false });
          this.r4DebitForm.controls.otp.enable({ emitEvent: false });
          this.messageService.add({
            severity: 'success',
            summary: 'OTP enviado',
            detail:
              resp?.message ||
              'El cashin quedó guardado. Ingresa el OTP para confirmar.',
          });
        },
        error: (err) => {
          this.r4DebitLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'R4',
            detail:
              err?.error?.message ||
              err?.message ||
              'No se pudo solicitar el OTP.',
          });
        },
      });
  }

  confirmR4Debit() {
    const otp = String(this.r4DebitForm.controls.otp.value || '').trim();
    if (!this.r4CashinId || !/^\d{8}$/.test(otp) || this.r4DebitLoading) {
      this.messageService.add({
        severity: 'error',
        summary: 'OTP',
        detail: 'Ingresa el OTP de 8 dígitos.',
      });
      return;
    }
    this.r4DebitLoading = true;
    this.transactionsService
      .r4ImmediateDebitConfirm({ cashin_id: this.r4CashinId, otp })
      .subscribe({
        next: (resp) => {
          this.r4DebitLoading = false;
          const status = resp?.data?.status;
          if (resp?.success === false || status === 'rejected') {
            this.messageService.add({
              severity: 'error',
              summary: 'Débito inmediato',
              detail: resp?.message || 'R4 rechazó el débito.',
            });
            return;
          }
          this.messageService.add({
            severity: status === 'finished' ? 'success' : 'info',
            summary: status === 'finished' ? 'Cashin finished' : 'Cashin en validación',
            detail:
              resp?.message ||
              (status === 'finished'
                ? 'El proveedor confirmó el débito.'
                : 'Aún no recibimos 200 AC00.'),
          });
          if (status === 'finished' || status === 'validating') {
            this.appService.triggerTransactionCreated('cashin');
            this.exitNewCashin();
          }
        },
        error: (err) => {
          this.r4DebitLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Débito inmediato',
            detail:
              err?.error?.message ||
              err?.message ||
              'No se pudo confirmar el débito.',
          });
        },
      });
  }

  continue = async () => {
    if (this.checkContinueDisabled()) return;

    if (this.newCashinType === 'Kushki') {
      this.showNewCashin = true;
      setTimeout(async () => {
        await this.buildKushkiInstance();
        await this.buildCardInstance();
      });
    }

    if (this.newCashinType === 'DECLARATION') {
      if (this.currency === 'EUR' || this.currency === 'COP') {
        this.setDeclaredTransactionType(this.transactionTypes[0]?.id ?? null);
      }
      this.preselectDeclaredCashinWallet();
      this.syncDeclaredCryptoReferenceValidator();
      this.loadDeclaredCryptoDepositSources();
      this.showNewCashin = true;
    }

    if (this.newCashinType === 'CSV') {
      this.showNewCashin = true;
    }

    if (this.isPayinLinkProviderService(this.selectedProvider)) {
      this.showNewCashin = true;
      this.loadSelectedPayinFeePreview();
      if (this.isSelectedDepaChannel) {
        this.preselectDepaDestinyWallet();
      }
      if (this.isSelectedNotbankProvider && this.notbankCashinNeedsBank) {
        this.loadNotbankBanksForCashin();
      }
      if (this.isSelectedNotbankProvider) {
        this.payinLinkCashinForm.controls.payment_method.setValue(
          this.defaultNotbankPayinMethod,
        );
      }
    }

    if (this.newCashinType === 'DEPA_IBAN') {
      this.showNewCashin = true;
      this.loadDepaDepositInstructions();
    }

    if (this.isR4ImmediateDebit) {
      this.r4DebitStep = 'data';
      this.r4CashinId = null;
      this.r4DebitForm.enable({ emitEvent: false });
      this.r4DebitForm.controls.otp.setValue('');
      this.showNewCashin = true;
      return;
    }

    if (
      this.newCashinType === 'POLYGON' ||
      this.newCashinType === 'TRC20' ||
      this.newCashinType === 'ERC20' ||
      this.newCashinType === 'BITCOIN' ||
      this.newCashinType === 'R4' ||
      this.newCashinType === 'BancoActivo' ||
      this.newCashinType === 'BancoPlaza'
    ) {
      this.appService.triggerViewWallets(
        true,
        'wallet',
        this.amount,
        this.currency,
        this.selectedProvider.metadata,
      );
    }
  };

  getWallets(currency: string | null) {
    return getSubwalletsForCurrency(
      currency,
      this.userService.user_balance_config,
      this.appService.config,
    );
  }

  getDeclaredCashinWallets(currency: string | null) {
    const wallets = this.getWallets(currency);
    if (normalizeCurrencyCode(currency) !== 'USDT') {
      return wallets;
    }

    const depositWallets = wallets.filter(
      (wallet) =>
        normalizeCurrencyCode(wallet.id) !== this.CBPAY_USDT_WALLET_ID,
    );
    return depositWallets.length ? depositWallets : wallets;
  }

  getPayinDestinyWallets(currency: string | null) {
    const wallets = this.getWallets(currency);
    if (this.isSelectedDepaChannel) {
      const depaWallets = wallets.filter(
        (w) => normalizeCurrencyCode(w.id) === 'DEPA',
      );
      return depaWallets.length ? depaWallets : wallets;
    }
    return wallets;
  }

  private preselectDepaDestinyWallet(): void {
    const depaWallet = this.getPayinDestinyWallets(this.currency).find(
      (w) => normalizeCurrencyCode(w.id) === 'DEPA',
    );
    if (depaWallet) {
      this.payinLinkCashinForm.controls.wallet.setValue(depaWallet);
    }
  }

  get declarationNetAmount(): number | null {
    const amount = Number(this.amount);
    const commission = Number(
      this.declarationCashinForm.controls.commission.value ?? 0,
    );

    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }

    const commissionNum = Number.isFinite(commission)
      ? Math.max(commission, 0)
      : 0;
    const net = amount - commissionNum;
    return net < 0 ? 0 : net;
  }

  get isCryptoDeclaredCashin(): boolean {
    return (
      this.newCashinType === 'DECLARATION' &&
      isCryptoDeclareCurrency(this.currency)
    );
  }

  get declaredCashinWalletLabel(): string {
    return this.isCryptoDeclaredCashin ? 'Red' : 'Wallet';
  }

  get declaredCashinWalletPlaceholder(): string {
    return this.isCryptoDeclaredCashin ? 'Elige la red' : 'Elige un wallet';
  }

  get declaredCashinReferenceLabel(): string {
    return this.isCryptoDeclaredCashin ? 'Hash de la transacción' : 'Referencia';
  }

  get declaredCashinReferencePlaceholder(): string {
    return this.isCryptoDeclaredCashin
      ? 'Ingresa el hash de la transacción'
      : 'Ingresa la referencia';
  }

  get declaredCashinSelectedWallet(): any {
    return this.declarationCashinForm.controls.wallet.value;
  }

  private resolveDeclaredCryptoDeposit() {
    return resolveDeclaredCryptoDeposit({
      wallet: this.declaredCashinSelectedWallet,
      providers: this.declaredCashinDepositProviders(),
      extraSources: [this.findAppWalletById(this.declaredCashinSelectedWallet?.id)],
      bitgoAddresses: this.declaredBitgoAddresses,
    });
  }

  get declaredCashinDepositAddress(): string {
    if (!this.isCryptoDeclaredCashin || !this.declaredCashinSelectedWallet) {
      return '';
    }
    return this.resolveDeclaredCryptoDeposit().address;
  }

  get declaredCashinDepositNetwork(): string {
    if (!this.declaredCashinSelectedWallet) {
      return '';
    }
    return this.resolveDeclaredCryptoDeposit().network;
  }

  private preselectDeclaredCashinWallet(): void {
    if (this.declarationCashinForm.controls.wallet.value) {
      return;
    }

    const wallets = this.getDeclaredCashinWallets(this.currency);
    if (wallets.length === 1) {
      this.declarationCashinForm.controls.wallet.setValue(wallets[0]);
    }
  }

  private syncDeclaredCryptoReferenceValidator(): void {
    const control = this.declarationCashinForm.controls.reference;
    if (this.isCryptoDeclaredCashin) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private declaredCashinClientId(): number | string | null {
    const clientValue = this.declarationCashinForm.controls.client_id.value as any;
    return clientValue?.user_id ?? clientValue ?? this.auth.user?.user_id ?? null;
  }

  onDeclaredCashinClientChanged(): void {
    this.loadDeclaredCryptoDepositSources();
  }

  private loadDeclaredCryptoDepositSources(): void {
    this.declaredBitgoSub?.unsubscribe();
    this.declaredBitgoAddresses = [];
    if (!this.isCryptoDeclaredCashin) {
      this.declaredCryptoAddressLoading = false;
      return;
    }

    const clientId = this.declaredCashinClientId();
    if (clientId == null) {
      this.declaredCryptoAddressLoading = false;
      return;
    }

    this.declaredCryptoAddressLoading = true;
    this.declaredBitgoSub = this.bitgoService
      .listWallets(clientId, { tree: true })
      .subscribe({
        next: (resp: any) => {
          this.declaredBitgoAddresses = flattenBitgoAddresses(resp);
          this.declaredCryptoAddressLoading = false;
        },
        error: () => {
          this.declaredBitgoAddresses = [];
          this.declaredCryptoAddressLoading = false;
        },
      });
  }

  private findAppWalletById(walletId: unknown): any {
    const id = normalizeCurrencyCode(walletId);
    if (!id) {
      return null;
    }

    return (
      (this.appService.wallets ?? []).find(
        (wallet: any) =>
          normalizeCurrencyCode(wallet?.id) === id ||
          normalizeCurrencyCode(wallet?.shortname) === id,
      ) ?? null
    );
  }

  private declaredCashinDepositProviders(): any[] {
    const currency = String(this.currency ?? '').trim().toUpperCase();
    return (this.providerServicesService.providerServices ?? []).filter(
      (ps: any) =>
        String(ps?.origin_coin_id ?? '').trim().toUpperCase() === currency &&
        String(ps?.service_id ?? '').trim().toLowerCase() === 'cashin' &&
        ps?.active !== false &&
        Number(ps?.active ?? 1) !== 0 &&
        !this.isPayinLinkProviderService(ps) &&
        !this.isCbpayUsdtCashinProvider(ps),
    );
  }

  getProviderChannelName(provider: any): string {
    if (!provider || provider?.id === this.DECLARATION_OPTION_ID) {
      return '';
    }
    if (provider?.id === this.DEPA_IBAN_OPTION_ID) {
      return 'Depasify';
    }
    if (provider?.id === this.DEPA_CARD_OPTION_ID) {
      return '';
    }
    if (this.isCbpayUsdtCashinProvider(provider)) {
      return 'Tarjeta';
    }
    const name = (
      provider?.provider?.username ??
      provider?.provider?.shortname ??
      provider?.username ??
      provider?.shortname ??
      ''
    )
      .toString()
      .trim();
    if (name) {
      return name;
    }
    if (this.isNotbankProvider(provider)) {
      return 'Notbank';
    }
    if (this.isBitsoCoProvider(provider) || this.isBitsoMxProvider(provider)) {
      return 'Bitso';
    }
    if (this.isMinteoCoProvider(provider) || this.isMinteoEurProvider(provider)) {
      return 'Minteo';
    }
    if (this.isDepaFiatProvider(provider)) {
      return 'Depasify';
    }
    return '';
  }

  getProviderCardTitle(provider: any): string {
    if (this.isCbpayUsdtCashinProvider(provider)) {
      return 'Pago con tarjeta';
    }
    if (provider?.metadata?.description) {
      return provider.metadata.description;
    }
    if (provider?.id === this.DEPA_IBAN_OPTION_ID) {
      return this.depaIbanOption.metadata.description;
    }
    if (this.isDepaFiatProvider(provider)) {
      return 'Depasify (open banking)';
    }
    return this.getProviderChannelName(provider) || 'Proveedor';
  }

  getProviderCardDisclaimer(provider: any): string {
    if (this.isCbpayUsdtCashinProvider(provider)) {
      return 'Genera un enlace seguro para pagar únicamente con tarjeta.';
    }
    return String(provider?.metadata?.disclaimer || '').trim();
  }

  get isSelectedDepaProvider(): boolean {
    return this.isDepaFiatProvider(this.selectedProvider);
  }

  get isSelectedDepaCard(): boolean {
    return this.selectedProvider?.id === this.DEPA_CARD_OPTION_ID;
  }

  get isSelectedDepaChannel(): boolean {
    return this.isSelectedDepaProvider || this.isSelectedDepaCard;
  }

  get isSelectedCbpayProvider(): boolean {
    return this.isCbpayUsdtCashinProvider(this.selectedProvider);
  }

  get isDepaIbanCashin(): boolean {
    return this.newCashinType === 'DEPA_IBAN';
  }

  isDepaFiatProvider(ps: any): boolean {
    if (!ps || ps?.id === this.DECLARATION_OPTION_ID) return false;
    if (ps?.id === this.DEPA_IBAN_OPTION_ID) return false;
    if (ps?.id === this.DEPA_CARD_OPTION_ID) return false;

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
    if (
      slug === 'depa' ||
      slug === 'depa_eur' ||
      slug === 'depa_gbp' ||
      slug === 'depa_usd'
    ) {
      return true;
    }
    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();
    return username.includes('depa');
  }

  loadDepaDepositInstructions(): void {
    this.depaDepositInstructions = null;
    this.depaDepositUnavailable = false;
    this.depaDepositLoading = true;

    const clientValue = this.payinLinkCashinForm.controls.client_id
      .value as any;
    const clientId =
      clientValue?.user_id ?? clientValue ?? this.auth.user?.user_id;

    this.transactionsService
      .getDepaDepositInstructions({
        currency: this.currency,
        client_id: clientId,
      })
      .subscribe({
        next: (resp: any) => {
          this.depaDepositLoading = false;
          const data = resp?.data ?? resp;
          if (data?.iban || data?.bank_account_id) {
            this.depaDepositInstructions = data;
            return;
          }
          this.depaDepositUnavailable = true;
          this.messageService.add({
            severity: 'warn',
            summary: 'Cuenta no disponible',
            detail:
              data?.message ||
              'La cuenta Depa no está lista. Operaciones puede solicitarla desde aquí o en Usuarios → Cuentas fiat.',
            life: 8000,
          });
        },
        error: (err) => {
          this.depaDepositLoading = false;
          this.depaDepositUnavailable = true;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              err?.error?.data?.message ||
              err?.error?.message ||
              err?.error ||
              err?.message ||
              'No se pudieron cargar las instrucciones de depósito.',
          });
        },
      });
  }

  depaRequestClientId(): number | string | null {
    const clientValue = this.payinLinkCashinForm?.controls?.client_id
      ?.value as any;
    return clientValue?.user_id ?? clientValue ?? this.auth.user?.user_id ?? null;
  }

  depaRequestUsername(): string {
    const clientValue = this.payinLinkCashinForm?.controls?.client_id
      ?.value as any;
    if (clientValue?.username) return clientValue.username;
    const userId = this.depaRequestClientId();
    const user = this.userService.users?.find(
      (u: any) => Number(u.user_id) === Number(userId),
    );
    return user?.username || '';
  }

  openDepaAccountRequest(): void {
    if (!this.depaRequestClientId()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cliente',
        detail: 'Selecciona un cliente antes de solicitar la cuenta Depa.',
      });
      return;
    }
    this.showDepaAccountRequest = true;
  }

  onDepaAccountProvisioned(): void {
    this.showDepaAccountRequest = false;
    this.depaDepositUnavailable = false;
    this.loadDepaDepositInstructions();
  }

  get isSelectedPayinLinkProvider(): boolean {
    return this.isPayinLinkProviderService(this.selectedProvider);
  }

  get isSelectedMinteoCoProvider(): boolean {
    return this.isMinteoCoProvider(this.selectedProvider);
  }

  get isSelectedMinteoEurProvider(): boolean {
    return this.isMinteoEurProvider(this.selectedProvider);
  }

  get isSelectedBitsoCoProvider(): boolean {
    return this.isBitsoCoProvider(this.selectedProvider);
  }

  get isSelectedBitsoMxProvider(): boolean {
    return this.isBitsoMxProvider(this.selectedProvider);
  }

  get isSelectedNotbankProvider(): boolean {
    return this.isNotbankProvider(this.selectedProvider);
  }

  /** Cashin Notbank needs bank_account when default pm is bank_transfer (COP/BRL/ARS). */
  get notbankCashinNeedsBank(): boolean {
    if (!this.isSelectedNotbankProvider) {
      return false;
    }
    const ccy = String(this.currency || '').toUpperCase();
    return ccy === 'COP' || ccy === 'BRL' || ccy === 'ARS';
  }

  get notbankCashinKindOptions(): { value: string; label: string }[] {
    const country = countryForNotbankCurrency(this.currency);
    return NOTBANK_KIND_OPTIONS[country] || [];
  }

  get notbankCashinIsPix(): boolean {
    return (
      String(this.payinLinkCashinForm.controls.notbank_kind.value || '') ===
      'br_pix'
    );
  }

  /** Short ops hint under the payin form title when Notbank is selected. */
  get notbankCashinHint(): string {
    if (!this.isSelectedNotbankProvider) {
      return '';
    }
    const ccy = String(this.currency || '').toUpperCase();
    if (ccy === 'CLP') {
      return 'Elige transferencia, tarjeta o caja vecina. Los tres devuelven un link de checkout para compartir. No hace falta cuenta bancaria en este paso.';
    }
    if (ccy === 'PEN') {
      return 'PEN usa QR por defecto: se devolverá el payload QR para mostrar al pagador. No hace falta banco ni provincia aquí.';
    }
    if (ccy === 'BRL') {
      return 'BRL usa transferencia/PIX: indica la cuenta o clave destino donde se registrará el depósito.';
    }
    if (ccy === 'ARS') {
      return 'ARS usa transferencia bancaria: indica CBU/CVU/alias o cuenta destino del depósito.';
    }
    if (ccy === 'COP') {
      return 'COP usa transferencia bancaria: indica banco, tipo y número de cuenta destino.';
    }
    return '';
  }

  get notbankPayinMethodOptions(): { value: string; label: string }[] {
    const ccy = String(this.currency || '').toUpperCase();
    if (ccy === 'CLP') {
      return [
        { value: 'assisted_transfer', label: 'Transferencia (checkout)' },
        { value: 'card', label: 'Tarjeta crédito/débito' },
        { value: 'cash', label: 'Caja vecina / efectivo' },
      ];
    }
    if (ccy === 'PEN') {
      return [
        { value: 'qr', label: 'Código QR' },
        { value: 'virtual_wallet', label: 'Billetera virtual' },
      ];
    }
    return [];
  }

  get showNotbankPayinMethod(): boolean {
    return (
      this.isSelectedNotbankProvider && this.notbankPayinMethodOptions.length > 0
    );
  }

  get defaultNotbankPayinMethod(): string {
    const ccy = String(this.currency || '').toUpperCase();
    if (ccy === 'CLP') return 'assisted_transfer';
    if (ccy === 'PEN') return 'qr';
    if (this.notbankCashinNeedsBank) return 'bank_transfer';
    return '';
  }

  get notbankBankLabel(): string {
    const ccy = String(this.currency || '').toUpperCase();
    if (ccy === 'BRL') return 'Banco destino (Brasil)';
    if (ccy === 'ARS') return 'Banco destino (Argentina)';
    if (ccy === 'COP') return 'Banco destino (Colombia)';
    return 'Banco destino';
  }

  get notbankKindLabel(): string {
    if (String(this.currency || '').toUpperCase() === 'BRL') {
      return 'Método de cobro (PIX o cuenta)';
    }
    return 'Tipo de cuenta';
  }

  get notbankNumberLabel(): string {
    const ccy = String(this.currency || '').toUpperCase();
    if (this.notbankCashinIsPix) return 'Clave PIX';
    if (ccy === 'ARS') return 'CBU / CVU / alias o número de cuenta';
    if (ccy === 'BRL') return 'Número de cuenta bancaria';
    return 'Número de cuenta';
  }

  get notbankNumberPlaceholder(): string {
    if (this.notbankCashinIsPix) {
      return 'Email, CPF, teléfono o EVP según el tipo de clave';
    }
    if (String(this.currency || '').toUpperCase() === 'BRL') {
      return 'Número de cuenta sin agencia ni DV';
    }
    if (String(this.currency || '').toUpperCase() === 'ARS') {
      return 'CBU (22 dígitos), CVU o alias';
    }
    return 'Número de cuenta';
  }

  get payerPhonePlaceholder(): string {
    const map: Record<string, string> = {
      CLP: '+56...',
      BRL: '+55...',
      PEN: '+51...',
      ARS: '+54...',
      COP: '+57...',
    };
    return map[String(this.currency || '').toUpperCase()] || '+...';
  }

  isNotbankProvider(ps: any): boolean {
    if (!ps) return false;
    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
    if (slug.includes('notbank')) {
      return true;
    }
    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();
    return username.includes('notbank');
  }

  loadNotbankBanksForCashin(): void {
    this.loadingNotbankBanks = true;
    this.notbankService.getBanks({ currency: this.currency }).subscribe({
      next: (resp) => {
        this.loadingNotbankBanks = false;
        this.notbankBanks = unwrapNotbankList(resp).map((b: any) => ({
          ...b,
          label: b.name || b.bank_name || b.id,
        }));
      },
      error: () => {
        this.loadingNotbankBanks = false;
        this.notbankBanks = [];
        this.messageService.add({
          severity: 'warn',
          summary: 'Bancos Notbank',
          detail: 'No se pudo cargar el listado de bancos. Reintenta.',
        });
      },
    });
  }

  private resetPayinLinkForm() {
    this.depaCardFeeRequestId += 1;
    this.depaCardFeePreview = null;
    this.depaCardFeeLoading = false;
    this.depaCardFeeError = '';
    this.cbpayFeeRequestId += 1;
    this.cbpayFeePreview = null;
    this.cbpayFeeLoading = false;
    this.cbpayFeeError = '';
    this.cbpayIdempotencyKey = '';
    this.payinLinkCashinForm.reset({
      client_id: !this.auth.hasRole(Roles.Admin)
        ? this.auth.user.user_id
        : null,
      wallet: null,
      payer_name: '',
      payer_first_name: '',
      payer_last_name: '',
      payer_email: '',
      payer_phone: '',
      payer_address: '',
      payer_postal_code: '',
      payer_city: '',
      payer_country: '',
      payer_country_code: '',
      payer_state: '',
      pse_bank: null,
      third_party_full_name: '',
      third_party_legal_identifier_type: 'CC',
      third_party_legal_identifier: '',
      third_party_email: '',
      third_party_phone_number: '',
      notbank_bank: null,
      notbank_kind: '',
      notbank_number: '',
      notbank_pix_type: '',
      notbank_agency: '',
      notbank_dv: '',
      payment_method: this.defaultNotbankPayinMethod,
    });
    if (this.isSelectedCbpayProvider) {
      this.prefillCbpayPayerFromSelectedClient();
    }
  }

  private selectedPayinClient(): any {
    const selected = this.payinLinkCashinForm.controls.client_id.value as any;
    if (selected && typeof selected === 'object') {
      return selected;
    }

    const selectedId = Number(selected);
    if (Number(this.auth.user?.user_id) === selectedId) {
      return this.auth.user;
    }

    return (this.userService.users || []).find(
      (user: any) => Number(user?.user_id) === selectedId,
    );
  }

  private userInfoForPayinClient(client: any): any {
    let metadata = client?.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch {
        metadata = {};
      }
    }
    return metadata?.user_info || {};
  }

  private prefillCbpayPayerFromSelectedClient(): void {
    const client = this.selectedPayinClient();
    if (!client) return;

    const userInfo = this.userInfoForPayinClient(client);
    this.payinLinkCashinForm.patchValue({
      payer_first_name: String(userInfo?.first_name || '').trim(),
      payer_last_name: String(
        userInfo?.last_name || userInfo?.surname || '',
      ).trim(),
      payer_email: String(userInfo?.email || '').trim(),
      payer_phone: String(
        userInfo?.phone || userInfo?.phone_number || '',
      ).trim(),
      payer_address: String(
        userInfo?.address || userInfo?.address_line_1 || '',
      ).trim(),
      payer_city: String(userInfo?.city || '').trim(),
      payer_country: String(userInfo?.country || '').trim(),
      payer_state: String(
        userInfo?.state || userInfo?.region || userInfo?.administrative_area || '',
      ).trim(),
      payer_postal_code: String(
        userInfo?.postal_code || userInfo?.zip_code || userInfo?.zipcode || '',
      ).trim(),
    });
  }

  loadDepaCardFeePreview(): void {
    const requestId = ++this.depaCardFeeRequestId;
    this.depaCardFeePreview = null;
    this.depaCardFeeError = '';

    if (!this.isSelectedDepaCard) {
      this.depaCardFeeLoading = false;
      return;
    }

    const clientValue = this.payinLinkCashinForm.controls.client_id.value as any;
    const clientId = clientValue?.user_id ?? clientValue;
    const net = this.roundCardAmount(Number(this.amount));
    const providerServiceId =
      this.selectedProvider?.provider_service_id ?? this.selectedProvider?.id;

    if (!clientId) {
      this.depaCardFeeLoading = false;
      this.depaCardFeeError = 'Selecciona un cliente para calcular la comisión.';
      return;
    }
    if (!Number.isFinite(net) || net <= 0 || !providerServiceId) {
      this.depaCardFeeLoading = false;
      this.depaCardFeeError = 'No se pudo calcular la comisión para este monto.';
      return;
    }

    this.depaCardFeeLoading = true;
    this.feesService
      .getEffectiveFees(
        1,
        500,
        {
          ...(this.auth.hasRole(Roles.Admin)
            ? { user_id: String(clientId) }
            : {}),
          coin_front: this.currency,
          coin_to: this.currency,
          transaction_type: 'cashin',
          transaction_subtype: 'card',
          provider_service_id: String(providerServiceId),
        },
        false,
      )
      .then(
        (response: any) => {
          if (requestId !== this.depaCardFeeRequestId) return;
          const rows = Array.isArray(response?.data) ? response.data : [];
          const applicable = rows
            .filter(
              (row: EffectiveFeeRow) =>
                String(row.coin_front || '').trim().toUpperCase() ===
                  this.currency.toUpperCase() &&
                String(row.coin_to || '').trim().toUpperCase() ===
                  this.currency.toUpperCase() &&
                String(row.transaction_type || '').trim().toLowerCase() ===
                  'cashin' &&
                String(row.transaction_subtype || '').trim().toLowerCase() ===
                  'card' &&
                String(row.provider_service_id || '').trim() ===
                  String(providerServiceId).trim() &&
                this.depaCardFeeAppliesToAmount(row, net),
            )
            .sort(
              (left: EffectiveFeeRow, right: EffectiveFeeRow) =>
                this.feeNumber(right.min_volume) -
                this.feeNumber(left.min_volume),
            );

          const feeRow = applicable[0];
          if (!feeRow) {
            this.depaCardFeeError =
              'No hay una comisión de tarjeta configurada para este cliente, moneda y monto.';
            this.depaCardFeeLoading = false;
            return;
          }

          const percentage = this.feeNumber(feeRow.user_fee);
          const flat = this.feeNumber(feeRow.user_flat_fee);
          const minimum = this.feeNumber(feeRow.user_min_cost);
          const maximum = this.feeNumber(feeRow.user_max_cost);
          if ([percentage, flat, minimum, maximum].some((value) => value < 0)) {
            this.depaCardFeeError =
              'La comisión de tarjeta configurada no es válida.';
            this.depaCardFeeLoading = false;
            return;
          }

          const fraction = percentage / 100;
          if (fraction >= 1) {
            this.depaCardFeeError =
              'El porcentaje de la comisión debe ser menor que 100 %.';
            this.depaCardFeeLoading = false;
            return;
          }

          let gross = (net + flat) / (1 - fraction);
          let fee = gross - net;
          if (minimum > 0) fee = Math.max(fee, minimum);
          if (maximum > 0) fee = Math.min(fee, maximum);
          gross = this.ceilCardAmount(net + fee);
          fee = this.roundCardFee(gross - net);

          this.depaCardFeePreview = {
            currency: this.currency.toUpperCase(),
            gross,
            fee,
            net,
            percentage,
            flat,
          };
          this.depaCardFeeLoading = false;
        },
        () => {
          if (requestId !== this.depaCardFeeRequestId) return;
          this.depaCardFeeLoading = false;
          this.depaCardFeeError =
            'No se pudo consultar la comisión de tarjeta. Reintenta.';
        },
      );
  }

  loadSelectedPayinFeePreview(): void {
    this.loadDepaCardFeePreview();
    this.loadCbpayFeePreview();
  }

  onPayinLinkClientChanged(): void {
    this.cbpayIdempotencyKey = '';
    if (this.isSelectedCbpayProvider) {
      this.prefillCbpayPayerFromSelectedClient();
    }
    this.loadSelectedPayinFeePreview();
  }

  loadCbpayFeePreview(): void {
    const requestId = ++this.cbpayFeeRequestId;
    this.cbpayFeePreview = null;
    this.cbpayFeeError = '';

    if (!this.isSelectedCbpayProvider) {
      this.cbpayFeeLoading = false;
      return;
    }

    const clientValue = this.payinLinkCashinForm.controls.client_id.value as any;
    const clientId = clientValue?.user_id ?? clientValue;
    const net = this.roundUsdtAmount(Number(this.amount));
    const providerServiceId =
      this.selectedProvider?.provider_service_id ?? this.selectedProvider?.id;

    if (!clientId) {
      this.cbpayFeeLoading = false;
      this.cbpayFeeError = 'Selecciona un cliente para calcular la comisión.';
      return;
    }
    if (!Number.isFinite(net) || net <= 0 || !providerServiceId) {
      this.cbpayFeeLoading = false;
      this.cbpayFeeError = 'No se pudo calcular la comisión para este monto.';
      return;
    }

    this.cbpayFeeLoading = true;
    this.feesService
      .getEffectiveFees(
        1,
        500,
        {
          ...(this.auth.hasRole(Roles.Admin)
            ? { user_id: String(clientId) }
            : {}),
          coin_front: 'USDT',
          coin_to: 'USDT',
          transaction_type: 'cashin',
          service_provider: 'CBPAY',
          provider_id: String(AddCashinComponent.CBPAY_PROVIDER_ID),
          provider_service_id: String(providerServiceId),
        },
        false,
      )
      .then(
        (response: any) => {
          if (requestId !== this.cbpayFeeRequestId) return;
          const rows = Array.isArray(response?.data) ? response.data : [];
          const applicable = rows
            .filter(
              (row: EffectiveFeeRow) =>
                String(row.coin_front || '').trim().toUpperCase() === 'USDT' &&
                String(row.coin_to || '').trim().toUpperCase() === 'USDT' &&
                String(row.transaction_type || '').trim().toLowerCase() ===
                  'cashin' &&
                !String(row.transaction_subtype || '').trim() &&
                (!String(row.service_provider || '').trim() ||
                  String(row.service_provider || '').trim().toUpperCase() ===
                    'CBPAY') &&
                (!String(row.provider_id || '').trim() ||
                  String(row.provider_id || '').trim() ===
                    String(AddCashinComponent.CBPAY_PROVIDER_ID)) &&
                String(row.provider_service_id || '').trim() ===
                  String(providerServiceId).trim() &&
                this.depaCardFeeAppliesToAmount(row, net),
            )
            .sort(
              (left: EffectiveFeeRow, right: EffectiveFeeRow) =>
                this.feeNumber(right.min_volume) -
                this.feeNumber(left.min_volume),
            );

          const feeRow = applicable[0];
          if (!feeRow) {
            this.cbpayFeeError =
              'No hay una comisión activa para el cobro USDT con tarjeta y este monto.';
            this.cbpayFeeLoading = false;
            return;
          }
          if (String(feeRow.billing_mode || '').trim().toLowerCase() !== 'end_user') {
            this.cbpayFeeError =
              'La comisión debe usar el modo de cobro al usuario final.';
            this.cbpayFeeLoading = false;
            return;
          }

          const fraction = this.feeNumber(feeRow.user_fee);
          const flat = this.feeNumber(feeRow.user_flat_fee);
          const minimum = this.feeNumber(feeRow.user_min_cost);
          const maximum = this.feeNumber(feeRow.user_max_cost);
          if ([fraction, flat, minimum, maximum].some((value) => value < 0)) {
            this.cbpayFeeError = 'La comisión configurada no es válida.';
            this.cbpayFeeLoading = false;
            return;
          }
          if (fraction >= 1) {
            this.cbpayFeeError =
              'La fracción porcentual de la comisión debe ser menor que 1.';
            this.cbpayFeeLoading = false;
            return;
          }

          let fee = net * fraction + flat;
          if (minimum > 0) fee = Math.max(fee, minimum);
          if (maximum > 0) fee = Math.min(fee, maximum);
          const gross = this.ceilUsdtAmount(net + fee);
          fee = this.roundCardFee(gross - net);

          this.cbpayFeePreview = {
            currency: 'USDT',
            gross,
            fee,
            net,
            percentage: fraction * 100,
            flat,
          };
          this.cbpayFeeLoading = false;
        },
        () => {
          if (requestId !== this.cbpayFeeRequestId) return;
          this.cbpayFeeLoading = false;
          this.cbpayFeeError =
            'No se pudo consultar la comisión. Reintenta.';
        },
      );
  }

  private depaCardFeeAppliesToAmount(
    row: EffectiveFeeRow,
    amount: number,
  ): boolean {
    const minimum = this.feeNumber(row.min_volume);
    const maximum = this.feeNumber(row.max_volume);
    return amount >= minimum && (maximum <= 0 || amount <= maximum);
  }

  private feeNumber(value: number | string | null | undefined): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private roundCardFee(value: number): number {
    return Math.round((value + Number.EPSILON) * 100000000) / 100000000;
  }

  private roundCardAmount(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private ceilCardAmount(value: number): number {
    return Math.ceil((value - Number.EPSILON) * 100) / 100;
  }

  private roundUsdtAmount(value: number): number {
    return Math.round((value + Number.EPSILON) * 1000000) / 1000000;
  }

  private ceilUsdtAmount(value: number): number {
    return Math.ceil((value - Number.EPSILON) * 1000000) / 1000000;
  }

  private normalizeDepaCardFeePreview(value: any): DepaCardFeePreview | null {
    const gross = Number(value?.gross_amount);
    const fee = Number(value?.fee_amount);
    const net = Number(value?.net_amount);
    if (![gross, fee, net].every((amount) => Number.isFinite(amount))) {
      return null;
    }
    return {
      currency: String(value?.currency || this.currency).trim().toUpperCase(),
      gross,
      fee,
      net,
      percentage: this.feeNumber(value?.percentage),
      flat: this.feeNumber(value?.flat),
    };
  }

  get isVesDeclaredCashin(): boolean {
    return this.currency === 'VES' && this.newCashinType === 'DECLARATION';
  }

  get isEurDeclaredCashin(): boolean {
    return this.currency === 'EUR' && this.newCashinType === 'DECLARATION';
  }

  get isCopDeclaredCashin(): boolean {
    return this.currency === 'COP' && this.newCashinType === 'DECLARATION';
  }

  get isAedDeclaredCashin(): boolean {
    return this.currency === 'AED' && this.newCashinType === 'DECLARATION';
  }

  get isUsdDeclaredCashin(): boolean {
    return this.currency === 'USD' && this.newCashinType === 'DECLARATION';
  }

  get isMxnDeclaredCashin(): boolean {
    return this.currency === 'MXN' && this.newCashinType === 'DECLARATION';
  }

  get hasDeclaredCashinExtraFields(): boolean {
    return (
      this.isEurDeclaredCashin ||
      this.isCopDeclaredCashin ||
      this.isAedDeclaredCashin ||
      this.isUsdDeclaredCashin ||
      this.isMxnDeclaredCashin
    );
  }

  get isVesBankTransferSelected(): boolean {
    return this.isBankTransfer(
      this.declarationCashinForm.controls.transaction_type.value,
    );
  }

  getBanks() {
    if (this.currency === 'VES') {
      return (
        this.appService.config?.currencies?.find(
          (el: any) => (el.id ?? el.currency) === this.currency,
        )?.subwallets ?? []
      );
    }

    return (
      this.userService.user_balance_config.currencies.find(
        (el: any) => el.id === this.currency,
      )?.subwallets ?? []
    );
  }

  setVesDocuType(type: string) {
    this.declarationCashinForm.controls.id_type.setValue(type);
  }

  setVesAccountType(type: string) {
    this.declarationCashinForm.controls.account_type.setValue(type);
  }

  setVesTransactionType(type: string | null) {
    this.setDeclaredTransactionType(type);
  }

  setDeclaredTransactionType(type: string | null) {
    this.declarationCashinForm.controls.transaction_type.setValue(type);
    if (!this.isBankTransfer(type)) {
      this.declarationCashinForm.controls.account_type.setValue('');
    }
  }

  pickVesBank(event: any) {
    const selected = event?.value ?? event;
    this.declarationCashinForm.controls.company_id.setValue(selected);
  }

  saveDeclaredCashin() {
    if (this.processing || this.declarationCashinForm.invalid) {
      return;
    }

    const providerServiceId = this.resolveDeclaredProviderServiceId();
    if (!providerServiceId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se encontró un provider service para declarar este cashin.',
      });
      return;
    }

    const amount = Number(this.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ingresa un monto válido.',
      });
      return;
    }

    const commissionRaw = this.declarationCashinForm.controls.commission.value;
    const commission =
      commissionRaw != null && String(commissionRaw).trim() !== ''
        ? Number(commissionRaw)
        : 0;
    const normalizedCommission =
      Number.isFinite(commission) && commission >= 0 ? commission : 0;
    const clientValue = this.declarationCashinForm.controls.client_id
      .value as any;
    const clientId = clientValue?.user_id ?? clientValue;
    const metadata = this.buildDeclaredCashinMetadata();

    if (
      (this.isCryptoDeclaredCashin ||
        this.currency === 'VES' ||
        this.currency === 'EUR' ||
        this.currency === 'COP' ||
        this.currency === 'AED' ||
        this.currency === 'USD' ||
        this.currency === 'MXN') &&
      !metadata
    ) {
      return;
    }

    const body: any = {
      metadata: metadata ? JSON.stringify(metadata) : null,
      destiny_wallet_id:
        this.declarationCashinForm.controls.wallet.value?.['id'],
      amount: amount.toString(),
      provider_service_id: providerServiceId,
      client_id: clientId,
      currency: this.currency,
      type: 'cashin',
      status:
        this.currency === 'VES'
          ? 'processing'
          : this.isCryptoDeclaredCashin
            ? 'validating'
            : 'finished',
    };

    if (normalizedCommission > 0) {
      body.commission = normalizedCommission;
    }

    this.processing = true;
    this.addCashin([body], { declared: true });
  }

  private buildDeclaredCashinMetadata(): Record<string, unknown> | null {
    const reference = (
      this.declarationCashinForm.controls.reference.value ?? ''
    )
      .toString()
      .trim();

    if (this.isCryptoDeclaredCashin) {
      if (!reference) {
        this.messageService.add({
          severity: 'error',
          summary: 'Hash obligatorio',
          detail: 'Ingresa el hash de la transacción para declarar este cashin.',
        });
        return null;
      }

      const deposit = this.resolveDeclaredCryptoDeposit();
      return buildCryptoDeclarationMetadata({
        reference,
        wallet: this.declaredCashinSelectedWallet,
        address: deposit.address,
        network: deposit.network,
      });
    }

    if (
      this.currency !== 'VES' &&
      this.currency !== 'EUR' &&
      this.currency !== 'COP' &&
      this.currency !== 'AED' &&
      this.currency !== 'USD' &&
      this.currency !== 'MXN'
    ) {
      return reference ? { reference } : null;
    }

    if (this.currency === 'EUR') {
      return this.buildEurDeclaredCashinMetadata(reference);
    }

    if (this.currency === 'COP') {
      return this.buildCopDeclaredCashinMetadata(reference);
    }

    if (this.currency === 'AED') {
      return this.buildAedDeclaredCashinMetadata(reference);
    }

    if (this.currency === 'USD') {
      return this.buildUsdDeclaredCashinMetadata(reference);
    }

    if (this.currency === 'MXN') {
      return this.buildMxnDeclaredCashinMetadata(reference);
    }

    // VES: matching por monto + referencia. Solo se pide la referencia.
    if (!reference) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos VES incompletos',
        detail: 'La referencia es obligatoria para cashins en VES.',
      });
      return null;
    }

    return { reference };
  }

  private buildAedDeclaredCashinMetadata(
    reference: string,
  ): Record<string, unknown> | null {
    const form = this.declarationCashinForm.controls;
    const firstname = (form.firstname.value ?? '').toString().trim();
    const lastname = (form.lastname.value ?? '').toString().trim();
    const accountId = (form.account_id.value ?? '').toString().trim();
    const purpose = (form.purpose.value ?? '').toString().trim();
    const beneficiaryType = (form.beneficiary_type.value ?? 'individual')
      .toString()
      .trim();
    const rail = (form.rail.value ?? 'iban').toString().trim();
    const iban = (form.iban.value ?? '').toString().trim();
    const wireAccount = (form.wire_account.value ?? '').toString().trim();
    const wireRouting = (form.wire_routing.value ?? '').toString().trim();
    const addressCountryCode = (form.address_country_code.value ?? '')
      .toString()
      .trim()
      .toUpperCase();
    const addressLine1 = (form.address_line_1.value ?? '').toString().trim();
    const externalReference = (form.external_reference.value ?? '')
      .toString()
      .trim();
    const hasRailData =
      rail === 'iban' ? Boolean(iban) : Boolean(wireAccount && wireRouting);

    if (
      !firstname ||
      !lastname ||
      !accountId ||
      !purpose ||
      !addressCountryCode ||
      !addressLine1 ||
      !hasRailData
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos AED incompletos',
        detail:
          'Completa nombres, apellidos, cuenta Fuse/Lorum, propósito, dirección del beneficiario e IBAN o datos wire.',
      });
      return null;
    }

    const metadata: Record<string, unknown> = {
      firstname,
      lastname,
      account_id: accountId,
      purpose,
      external_reference: externalReference,
      beneficiary_type: beneficiaryType,
      rail,
      iban,
      wire_account: wireAccount,
      wire_routing: wireRouting,
      address_country_code: addressCountryCode,
      address_line_1: addressLine1,
    };

    if (reference) {
      metadata['reference'] = reference;
    }

    return metadata;
  }

  private buildUsdDeclaredCashinMetadata(
    reference: string,
  ): Record<string, unknown> | null {
    const form = this.declarationCashinForm.controls;
    const firstname = (form.firstname.value ?? '').toString().trim();
    const lastname = (form.lastname.value ?? '').toString().trim();
    const accountId = (form.account_id.value ?? '').toString().trim();
    const purpose = (form.purpose.value ?? '').toString().trim();
    const beneficiaryType = (form.beneficiary_type.value ?? 'individual')
      .toString()
      .trim();
    const rail = (form.rail.value ?? 'iban').toString().trim();
    const iban = (form.iban.value ?? '').toString().trim();
    const wireAccount = (form.wire_account.value ?? '').toString().trim();
    const wireRouting = (form.wire_routing.value ?? '').toString().trim();
    const bankBic = (form.bank_bic.value ?? '').toString().trim();
    const addressCountryCode = (form.address_country_code.value ?? '')
      .toString()
      .trim()
      .toUpperCase();
    const addressLine1 = (form.address_line_1.value ?? '').toString().trim();
    const externalReference = (form.external_reference.value ?? '')
      .toString()
      .trim();
    const hasRailData =
      rail === 'iban' ? Boolean(iban) : Boolean(wireAccount && wireRouting);

    if (
      !firstname ||
      !lastname ||
      !accountId ||
      !purpose ||
      !bankBic ||
      !addressCountryCode ||
      !addressLine1 ||
      !hasRailData
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos USD incompletos',
        detail:
          'Completa nombres, apellidos, cuenta Fuse/Lorum, propósito, BIC, dirección del beneficiario e IBAN o datos wire.',
      });
      return null;
    }

    const metadata: Record<string, unknown> = {
      firstname,
      lastname,
      account_id: accountId,
      purpose,
      external_reference: externalReference,
      beneficiary_type: beneficiaryType,
      rail,
      iban,
      wire_account: wireAccount,
      wire_routing: wireRouting,
      bank_bic: bankBic,
      address_country_code: addressCountryCode,
      address_line_1: addressLine1,
    };

    if (reference) {
      metadata['reference'] = reference;
    }

    return metadata;
  }

  private buildMxnDeclaredCashinMetadata(
    reference: string,
  ): Record<string, unknown> | null {
    const form = this.declarationCashinForm.controls;
    const firstname = (form.firstname.value ?? '').toString().trim();
    const lastname = (form.lastname.value ?? '').toString().trim();
    const protocol = (form.protocol.value ?? 'clabe')
      .toString()
      .trim()
      .toLowerCase();
    const clabe = (form.clabe.value ?? '').toString().trim();
    const institutionCode = (form.institution_code.value ?? '')
      .toString()
      .trim();
    const rfc = (form.rfc.value ?? '').toString().trim().toUpperCase();
    const numericRef = (form.numeric_ref.value ?? '')
      .toString()
      .trim()
      .replace(/\D/g, '')
      .slice(0, 7);
    const notesRef = (form.notes_ref.value ?? '')
      .toString()
      .trim()
      .slice(0, 40);
    const needsInstitution =
      protocol === 'debitcard' || protocol === 'phonenum';

    if (
      !firstname ||
      !lastname ||
      !protocol ||
      !clabe ||
      (needsInstitution && !institutionCode)
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos MXN incompletos',
        detail:
          'Completa nombres, apellidos, protocolo SPEI, CLABE/tarjeta/teléfono y código de institución cuando aplique.',
      });
      return null;
    }

    const metadata: Record<string, unknown> = {
      firstname,
      lastname,
      protocol,
      clabe,
      institution_code: institutionCode,
      rfc,
      numeric_ref: numericRef,
      notes_ref: notesRef,
    };

    if (reference) {
      metadata['reference'] = reference;
    }

    return metadata;
  }

  private buildCopDeclaredCashinMetadata(
    reference: string,
  ): Record<string, unknown> | null {
    const selectedBank = this.declarationCashinForm.controls.company_id.value;
    const bankCode = this.extractBankCode(selectedBank);
    const bankName = this.extractBankName(selectedBank);
    const firstname = (
      this.declarationCashinForm.controls.firstname.value ?? ''
    )
      .toString()
      .trim();
    const lastname = (this.declarationCashinForm.controls.lastname.value ?? '')
      .toString()
      .trim();
    const transactionType = (
      this.declarationCashinForm.controls.transaction_type.value ?? ''
    )
      .toString()
      .trim();
    const documentValue = (
      this.declarationCashinForm.controls.id_number.value ?? ''
    )
      .toString()
      .trim();
    const accountNumber = (
      this.declarationCashinForm.controls.account_number.value ?? ''
    )
      .toString()
      .trim();

    if (
      !firstname ||
      !lastname ||
      !transactionType ||
      !documentValue ||
      !bankCode ||
      !bankName ||
      !accountNumber
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos COP incompletos',
        detail:
          'Completa tipo de transacción, nombres, apellidos, documento de identidad, banco de origen y cuenta origen.',
      });
      return null;
    }

    const parts = documentValue.split('-');
    const idType =
      parts.length >= 2 && parts[0].trim()
        ? parts[0].trim()
        : this.declarationCashinForm.controls.id_type.value;
    const idNumber =
      parts.length >= 2 ? parts.slice(1).join('-').trim() : documentValue;

    const metadata: Record<string, unknown> = {
      firstname,
      lastname,
      id_type: idType,
      id_number: idNumber,
      transaction_type: transactionType,
      company_name: bankName,
      company_id: bankCode,
      account_number: accountNumber,
    };

    if (reference) {
      metadata['reference'] = reference;
    }

    return metadata;
  }

  private buildEurDeclaredCashinMetadata(
    reference: string,
  ): Record<string, unknown> | null {
    const firstname = (
      this.declarationCashinForm.controls.firstname.value ?? ''
    )
      .toString()
      .trim();
    const lastname = (this.declarationCashinForm.controls.lastname.value ?? '')
      .toString()
      .trim();
    const transactionType = (
      this.declarationCashinForm.controls.transaction_type.value ?? ''
    )
      .toString()
      .trim();
    const accountNumber = (
      this.declarationCashinForm.controls.account_number.value ?? ''
    )
      .toString()
      .trim();

    if (!firstname || !lastname || !transactionType || !accountNumber) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos EUR incompletos',
        detail: 'Completa tipo de transacción, nombres, apellidos e IBAN.',
      });
      return null;
    }

    const metadata: Record<string, unknown> = {
      firstname,
      lastname,
      transaction_type: transactionType,
      account_number: accountNumber,
    };

    if (reference) {
      metadata['reference'] = reference;
    }

    return metadata;
  }

  createPayinLinkCashin() {
    if (!this.auth.guardTransactionalAction()) return;
    if (this.processing || !this.selectedProvider?.id) {
      return;
    }

    const amount = Number(this.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ingresa un monto válido.',
      });
      return;
    }

    const clientValue = this.payinLinkCashinForm.controls.client_id
      .value as any;
    const clientId = clientValue?.user_id ?? clientValue;

    if (!clientId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Debes seleccionar un cliente.',
      });
      return;
    }

    if (this.isSelectedDepaCard && !this.depaCardFeePreview) {
      this.messageService.add({
        severity: 'error',
        summary: 'Comisión no disponible',
        detail:
          this.depaCardFeeError ||
          'Espera a que se calcule la comisión antes de crear el link.',
      });
      return;
    }
    if (this.isSelectedCbpayProvider && !this.cbpayFeePreview) {
      this.messageService.add({
        severity: 'error',
        summary: 'Comisión no disponible',
        detail:
          this.cbpayFeeError ||
          'Espera a que se calcule la comisión antes de crear el link.',
      });
      return;
    }

    const body: any = {
      amount,
      provider_service_id:
        this.selectedProvider.provider_service_id ?? this.selectedProvider.id,
      client_id: clientId,
      currency: this.currency,
      final_url: this.resolvePayinLinkFinalUrl(),
      payer_name:
        this.payinLinkCashinForm.controls.payer_name.value || undefined,
      payer_email:
        this.payinLinkCashinForm.controls.payer_email.value || undefined,
      payer_phone:
        this.payinLinkCashinForm.controls.payer_phone.value || undefined,
    };
    if (this.isSelectedCbpayProvider) {
      body.idempotency_key = this.createPayinIdempotencyKey();
      body.payer_first_name =
        this.payinLinkCashinForm.controls.payer_first_name.value || undefined;
      body.payer_last_name =
        this.payinLinkCashinForm.controls.payer_last_name.value || undefined;
      body.payer_address =
        this.payinLinkCashinForm.controls.payer_address.value || undefined;
      body.payer_city =
        this.payinLinkCashinForm.controls.payer_city.value || undefined;
      body.payer_country =
        this.payinLinkCashinForm.controls.payer_country.value || undefined;
      body.payer_state =
        this.payinLinkCashinForm.controls.payer_state.value || undefined;
      body.payer_postal_code =
        this.payinLinkCashinForm.controls.payer_postal_code.value || undefined;
    }

    if (this.isSelectedMinteoEurProvider || this.isSelectedDepaChannel) {
      const walletId =
        this.payinLinkCashinForm.controls.wallet.value?.['id'] ?? null;
      if (!walletId) {
        this.messageService.add({
          severity: 'error',
          summary: this.isSelectedDepaCard ? 'Cuenta no disponible' : 'Error',
          detail: this.isSelectedDepaCard
            ? 'El cliente no tiene una cuenta de cobro disponible para esta moneda.'
            : 'Debes seleccionar la wallet destino.',
        });
        return;
      }
      body.destiny_wallet_id = walletId;
    }

    if (this.isSelectedDepaProvider) {
      const payerName = (body.payer_name || '').toString().trim();
      const payerEmail = (body.payer_email || '').toString().trim();
      if (!payerName || !payerEmail) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Nombre y email del pagador son obligatorios para open banking Depasify.',
        });
        return;
      }
    }

    if (this.isSelectedDepaCard) {
      const payerName = String(body.payer_name || '').trim();
      const payerEmail = String(body.payer_email || '').trim();
      const payerAddress = String(
        this.payinLinkCashinForm.controls.payer_address.value || '',
      ).trim();
      const payerPostalCode = String(
        this.payinLinkCashinForm.controls.payer_postal_code.value || '',
      ).trim();
      const payerCity = String(
        this.payinLinkCashinForm.controls.payer_city.value || '',
      ).trim();
      const payerCountryCode = String(
        this.payinLinkCashinForm.controls.payer_country_code.value || '',
      )
        .trim()
        .toUpperCase();
      if (
        payerName.split(/\s+/).length < 2 ||
        !payerEmail ||
        !payerAddress ||
        !payerPostalCode ||
        !payerCity ||
        !payerCountryCode
      ) {
        this.messageService.add({
          severity: 'error',
          summary: 'Datos incompletos',
          detail:
            'Para cobrar con tarjeta completa nombre y apellido, email, dirección, código postal, ciudad y país.',
        });
        return;
      }
      body.depa_cashin_mode = 'card';
      body.payer_address = payerAddress;
      body.payer_postal_code = payerPostalCode;
      body.payer_city = payerCity;
      body.payer_country_code = payerCountryCode;
      body.payer_state = String(
        this.payinLinkCashinForm.controls.payer_state.value || '',
      ).trim() || undefined;
    }

    if (this.isSelectedMinteoCoProvider) {
      const pseBank = this.payinLinkCashinForm.controls.pse_bank.value;
      const fullName = (
        this.payinLinkCashinForm.controls.third_party_full_name.value || ''
      )
        .toString()
        .trim();
      const legalId = (
        this.payinLinkCashinForm.controls.third_party_legal_identifier.value ||
        ''
      )
        .toString()
        .trim();
      const thirdEmail = (
        this.payinLinkCashinForm.controls.third_party_email.value || ''
      )
        .toString()
        .trim();
      const thirdPhone = (
        this.payinLinkCashinForm.controls.third_party_phone_number.value || ''
      )
        .toString()
        .trim();

      if (!pseBank || !fullName || !legalId || !thirdEmail || !thirdPhone) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Para PSE (Minteo Colombia) son obligatorios: banco, nombre completo, tipo y número de documento, email y teléfono del pagador.',
        });
        return;
      }

      body.payment = { method: 'PSE', bank: pseBank };
      body.third_party = {
        full_name: fullName,
        legal_identifier_type:
          this.payinLinkCashinForm.controls.third_party_legal_identifier_type
            .value || 'CC',
        legal_identifier: legalId,
        country_iso: 'CO',
        email: thirdEmail,
        phone_number: thirdPhone,
      };
    }

    if (this.isSelectedBitsoCoProvider) {
      const pseBank = this.payinLinkCashinForm.controls.pse_bank.value;
      const payerName = String(body.payer_name || '').trim();
      const docType = String(
        this.payinLinkCashinForm.controls.third_party_legal_identifier_type
          .value || 'CC',
      ).trim();
      const docId = String(
        this.payinLinkCashinForm.controls.third_party_legal_identifier.value ||
          '',
      ).trim();
      if (!pseBank) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Selecciona el banco de origen para el link de pago Bitso (PSE Colombia).',
        });
        return;
      }
      if (!payerName || !docId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Para Bitso PSE son obligatorios el nombre y el documento (CC/CE/NIT) del pagador.',
        });
        return;
      }
      const payerPhone = this.normalizeBitsoCoPhone(
        String(body.payer_phone || ''),
      );
      if (!payerPhone) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Ingresa un teléfono colombiano de 10 dígitos (ej. 3001234567 o +57 3001234567).',
        });
        return;
      }
      body.pse_bank = pseBank;
      body.payment = { bank: pseBank };
      body.document_type = docType;
      body.document_id = docId;
      body.payer_phone = payerPhone;
      body.third_party = {
        full_name: payerName,
        type: docType,
        legal_identifier_type: docType,
        legal_identifier: docId,
        country_iso: 'CO',
        email: String(body.payer_email || '').trim() || undefined,
        phone: payerPhone,
        phone_number: payerPhone,
      };
    }

    if (this.isSelectedBitsoMxProvider) {
      const payerName = String(body.payer_name || '').trim();
      if (!payerName) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'El nombre del pagador es obligatorio para cashin Bitso MXN (SPEI).',
        });
        return;
      }
    }

    if (this.isSelectedNotbankProvider && this.notbankCashinNeedsBank) {
      const bankObj = this.payinLinkCashinForm.controls.notbank_bank.value as any;
      const bankId = bankObj?.id || bankObj;
      const kind = (this.payinLinkCashinForm.controls.notbank_kind.value || '')
        .toString()
        .trim();
      const number = (this.payinLinkCashinForm.controls.notbank_number.value || '')
        .toString()
        .trim();
      const pixType = (
        this.payinLinkCashinForm.controls.notbank_pix_type.value || ''
      )
        .toString()
        .trim();
      const agency = (
        this.payinLinkCashinForm.controls.notbank_agency.value || ''
      )
        .toString()
        .trim();
      const dv = (this.payinLinkCashinForm.controls.notbank_dv.value || '')
        .toString()
        .trim();
      if (!bankId || !kind || !number) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Completa banco destino, tipo de cuenta/cobro y número o clave.',
        });
        return;
      }
      if (String(this.currency || '').toUpperCase() === 'BRL') {
        if (kind === 'br_pix' && !pixType) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Para PIX indica el tipo de clave (Email, CPF, Phone o EVP).',
          });
          return;
        }
        if (kind !== 'br_pix' && (!agency || !dv)) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              'Para cuenta bancaria BRL son obligatorios agencia y dígito verificador (DV).',
          });
          return;
        }
      }
      const nbBank: Record<string, unknown> = {
        bank: String(bankId),
        kind,
        number,
        currency: this.currency,
      };
      if (pixType) nbBank['pix_type'] = pixType;
      if (agency) nbBank['agency'] = agency;
      if (dv) nbBank['dv'] = dv;
      body.bank_account = nbBank;
      body.notbank_bank_account = nbBank; // legacy alias
      body.payment_method = 'bank_transfer';
    } else if (this.isSelectedNotbankProvider) {
      const pm = (
        this.payinLinkCashinForm.controls.payment_method.value ||
        this.defaultNotbankPayinMethod ||
        ''
      )
        .toString()
        .trim();
      if (pm) {
        body.payment_method = pm;
      }
    }

    if (this.isSelectedNotbankProvider) {
      this.ensureNotbankReadyThenCreatePayin(body);
      return;
    }

    this.submitPayinLinkRequest(body);
  }

  private buildNotbankBankFromPayinForm(): Record<string, unknown> | null {
    if (!this.notbankCashinNeedsBank) {
      return null;
    }
    const bankObj = this.payinLinkCashinForm.controls.notbank_bank.value as any;
    const bankId = bankObj?.id || bankObj;
    const kind = (this.payinLinkCashinForm.controls.notbank_kind.value || '')
      .toString()
      .trim();
    const number = (this.payinLinkCashinForm.controls.notbank_number.value || '')
      .toString()
      .trim();
    if (!bankId || !kind || !number) {
      return null;
    }
    const nbBank: Record<string, unknown> = {
      bank: String(bankId),
      kind,
      number,
      currency: this.currency,
    };
    const pixType = (
      this.payinLinkCashinForm.controls.notbank_pix_type.value || ''
    )
      .toString()
      .trim();
    if (pixType) nbBank['pix_type'] = pixType;
    const agency = (
      this.payinLinkCashinForm.controls.notbank_agency.value || ''
    )
      .toString()
      .trim();
    const dv = (this.payinLinkCashinForm.controls.notbank_dv.value || '')
      .toString()
      .trim();
    if (agency) nbBank['agency'] = agency;
    if (dv) nbBank['dv'] = dv;
    return nbBank;
  }

  private ensureNotbankReadyThenCreatePayin(body: any): void {
    const clientId = body.client_id;
    this.processing = true;
    this.notbankService.getAccount(clientId).subscribe({
      next: (resp) => {
        if (isNotbankAccountProvisioned(resp)) {
          this.submitPayinLinkRequest(body);
          return;
        }
        this.processing = false;
        this.openNotbankProvisionDialog(clientId, body, {
          message: 'El cliente no tiene cuenta hijo Notbank. Completa el alta.',
        });
      },
      error: (err) => {
        this.processing = false;
        // 404 / "No Notbank account provisioned" → open KYC dialog
        if (
          err?.status === 404 ||
          err?.error?.status === 404 ||
          isNotbankProvisionRequiredError(err)
        ) {
          const errBody = extractNotbankErrorBody(err);
          this.openNotbankProvisionDialog(clientId, body, {
            ...errBody,
            message:
              errBody['message'] ||
              'El cliente no tiene cuenta hijo Notbank. Completa el alta.',
          });
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Notbank',
          detail:
            extractNotbankErrorBody(err)['message'] ||
            err?.message ||
            'No se pudo verificar la cuenta Notbank del cliente.',
        });
      },
    });
  }

  private openNotbankProvisionDialog(
    clientId: any,
    pendingBody: any,
    errBody?: Record<string, any>,
  ): void {
    this.pendingPayinAfterProvision = pendingBody;
    this.notbankProvisionClientId = clientId;
    this.notbankProvisionMissing = Array.isArray(errBody?.['missing_fields'])
      ? errBody!['missing_fields']
      : [];
    this.notbankProvisionPartial =
      errBody?.['partial_register'] &&
      typeof errBody['partial_register'] === 'object'
        ? errBody['partial_register']
        : null;
    this.notbankProvisionBank =
      pendingBody?.notbank_bank_account || this.buildNotbankBankFromPayinForm();
    this.notbankProvisionError =
      errBody?.['message'] ||
      'Provisiona la cuenta hijo Notbank para continuar.';
    this.showNotbankProvision = true;
  }

  onNotbankProvisioned(_result: any): void {
    this.showNotbankProvision = false;
    const pending = this.pendingPayinAfterProvision;
    this.pendingPayinAfterProvision = null;
    if (pending) {
      this.submitPayinLinkRequest(pending);
    }
  }

  onNotbankProvisionCancelled(): void {
    this.showNotbankProvision = false;
    this.pendingPayinAfterProvision = null;
    this.processing = false;
  }

  private submitPayinLinkRequest(body: any): void {
    this.processing = true;
    this.transactionsService.createPayinLink(body).subscribe({
      next: (resp: any) => {
        this.processing = false;
        if (String(resp?.status || '').toLowerCase() === 'error') {
          this.messageService.add({
            severity: 'error',
            summary: 'Error del proveedor',
            detail: extractPayinLinkError(resp),
          });
          return;
        }
        const data = resp?.data ?? resp;
        this.cbpayIdempotencyKey = '';
        this.showNewCashin = false;
        this.showNewCashinType = false;
        this.appService.triggerTransactionCreated('cashin');
        this.handlePayinLinkCreatedSuccess(data);
      },
      error: (err) => {
        this.processing = false;
        if (
          this.isSelectedNotbankProvider &&
          isNotbankProvisionRequiredError(err)
        ) {
          this.openNotbankProvisionDialog(body.client_id, body, extractNotbankErrorBody(err));
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error del proveedor',
          detail: extractPayinLinkError(err),
        });
      },
    });
  }

  private createPayinIdempotencyKey(): string {
    if (this.cbpayIdempotencyKey) {
      return this.cbpayIdempotencyKey;
    }
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      this.cbpayIdempotencyKey = `transferswap-cashin-${crypto.randomUUID()}`;
      return this.cbpayIdempotencyKey;
    }
    this.cbpayIdempotencyKey = `transferswap-cashin-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`;
    return this.cbpayIdempotencyKey;
  }

  private resolveDeclaredProviderServiceId(): number | null {
    if (this.isCryptoDeclaredCashin) {
      const matchedId = parseProviderServiceId(
        this.resolveDeclaredCryptoDeposit().provider,
      );
      if (matchedId) {
        return matchedId;
      }
    }

    const currency = this.currency;
    const mappedByCurrency: Record<string, number> = {
      VES: 2,
      COP: 45,
      EUR: 42,
      USDT: 28,
      BTC: 62,
      ETH: 63,
      AED: 89,
      MXN: 90,
      USD: 188,
    };

    const mappedProviderId = mappedByCurrency[currency];
    if (mappedProviderId) {
      return mappedProviderId;
    }

    const firstActiveProvider = this.providers?.find(
      (provider: any) =>
        provider?.id && provider.id !== this.DECLARATION_OPTION_ID,
    );

    return firstActiveProvider?.id ?? null;
  }

  isPayinLinkProviderService(ps: any): boolean {
    if (!ps || ps?.id === this.DECLARATION_OPTION_ID) return false;
    if (ps?.id === this.DEPA_IBAN_OPTION_ID) return false;
    if (ps?.id === this.DEPA_CARD_OPTION_ID) return true;
    if (this.isCbpayUsdtCashinProvider(ps)) return true;

    const providerServiceId = Number(ps?.id ?? ps?.user_id ?? 0);
    if (
      providerServiceId === AddCashinComponent.MINTEO_COP_PROVIDER_SERVICE_ID ||
      providerServiceId ===
        AddCashinComponent.CLEARJUNCTION_EUR_PROVIDER_SERVICE_ID
    ) {
      return true;
    }

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();

    if (
      [
        'clearjunction',
        'minteo_co',
        'minteo_eur',
        'depa',
        'depa_eur',
        'depa_gbp',
        'depa_usd',
        'cop_funding',
        'mxn_funding',
        'bitso_co',
        'bitso_mx',
        'notbank',
        'zen',
      ].includes(slug) ||
      slug.includes('notbank')
    ) {
      return true;
    }

    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return AddCashinComponent.PAYIN_USERNAME_MATCH.some((match) =>
      username.includes(match),
    );
  }

  isCbpayUsdtCashinProvider(ps: any): boolean {
    if (!ps || ps?.id === this.DECLARATION_OPTION_ID) return false;
    const providerServiceId = Number(
      ps?.provider_service_id ?? ps?.id ?? 0,
    );
    if (
      providerServiceId !==
      AddCashinComponent.CBPAY_USDT_PROVIDER_SERVICE_ID
    ) {
      return false;
    }

    const providerId = Number(
      ps?.provider_id ?? ps?.provider?.user_id ?? ps?.user_id ?? 0,
    );
    const currency = String(ps?.origin_coin_id ?? '').trim().toUpperCase();
    const service = String(ps?.service_id ?? '').trim().toLowerCase();
    return (
      providerId === AddCashinComponent.CBPAY_PROVIDER_ID &&
      currency === 'USDT' &&
      service === 'cashin' &&
      ps?.active !== false &&
      Number(ps?.active ?? 1) !== 0
    );
  }

  isMinteoCoProvider(ps: any): boolean {
    if (!ps) return false;

    const providerServiceId = Number(ps?.id ?? ps?.user_id ?? 0);
    if (
      providerServiceId === AddCashinComponent.MINTEO_COP_PROVIDER_SERVICE_ID
    ) {
      return true;
    }

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();

    if (slug === 'minteo_co') {
      return true;
    }

    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return username.includes('minteo') && this.currency === 'COP';
  }

  isBitsoCoProvider(ps: any): boolean {
    if (!ps) return false;

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();

    if (slug === 'bitso_co' || slug === 'cop_funding') {
      return true;
    }

    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return username.includes('bitso') && this.currency === 'COP';
  }

  /** Bitso PSE: 10 Colombian digits (strips +57). */
  normalizeBitsoCoPhone(raw: string): string {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.startsWith('57') && digits.length >= 12) {
      return digits.slice(-10);
    }
    return digits.length === 10 ? digits : '';
  }

  isBitsoMxProvider(ps: any): boolean {
    if (!ps) return false;

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();

    if (slug === 'bitso_mx' || slug === 'mxn_funding') {
      return true;
    }

    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return username.includes('bitso') && this.currency === 'MXN';
  }

  isMinteoEurProvider(ps: any): boolean {
    if (!ps) return false;

    const slug = (
      ps?.provider?.metadata?.provider_slug ??
      ps?.metadata?.provider_slug ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();

    if (slug === 'minteo_eur') {
      return true;
    }

    const username = (ps?.provider?.username ?? ps?.username ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return username.includes('minteo') && this.currency === 'EUR';
  }

  get payinCreatedModalHeader(): string {
    if (this.payinCreatedQrValue) return 'QR de pago creado';
    if (this.payinCreatedDepositInstructions) return 'Datos para el depósito';
    if (this.payinCreatedManualValidation) return 'Cashin en validación manual';
    return 'Link de pago creado';
  }

  get maskedPayinCreatedAppLink(): string {
    const value = String(this.payinCreatedAppLink || '').trim();
    if (!value) return '';
    const token = value.split('/').filter(Boolean).pop() || '';
    const suffix = token.slice(-8);
    return suffix ? `Enlace seguro ••••••••${suffix}` : 'Enlace seguro de pago';
  }

  copyPayinCreatedLink(): void {
    if (!this.payinCreatedAppLink) return;
    this.utilsService.copyToClipboard(this.payinCreatedAppLink);
    this.messageService.add({
      severity: 'success',
      summary: 'Enlace copiado',
      detail: 'Ya puedes compartirlo con el pagador.',
      life: 3000,
    });
  }

  openPayinCreatedLink(): void {
    const value = String(this.payinCreatedAppLink || '').trim();
    if (!value.startsWith('https://') && !value.startsWith('http://')) return;
    window.open(value, '_blank', 'noopener,noreferrer');
  }

  closePayinLinkCreatedModal() {
    this.showPayinLinkCreatedModal = false;
    this.payinCreatedAppLink = null;
    this.payinCreatedQrValue = null;
    this.payinCreatedQrDataUrl = null;
    this.payinCreatedDepositInstructions = null;
    this.payinCreatedManualValidation = false;
    this.payinCreatedProviderError = '';
    this.payinCreatedMessage = '';
    this.payinCreatedTransactionId = null;
    this.payinCreatedFee = null;
    this.payinCreatedCardCharge = null;
    this.closeAddModal();
  }

  private async handlePayinLinkCreatedSuccess(data: any) {
    const view = parsePayinCreatedView(data);
    const instrument = data?.payment_instrument ?? data?.paymentInstrument;
    const instrumentType = String(instrument?.type || '')
      .trim()
      .toLowerCase();
    const instrumentValue = String(instrument?.value || '').trim();

    this.payinCreatedDepositInstructions = view.instructions;
    this.payinCreatedManualValidation = view.isManualValidation;
    this.payinCreatedProviderError = view.providerError;
    this.payinCreatedMessage = view.message;
    this.payinCreatedTransactionId = view.transactionId;
    this.payinCreatedFee = this.normalizeDepaCardFeePreview(data?.fee);
    const cardChargeAmount = Number(data?.card_charge?.amount);
    const cardChargeCurrency = String(
      data?.card_charge?.currency || '',
    ).trim().toUpperCase();
    this.payinCreatedCardCharge =
      Number.isFinite(cardChargeAmount) &&
      cardChargeAmount > 0 &&
      cardChargeCurrency
        ? { amount: cardChargeAmount, currency: cardChargeCurrency }
        : null;

    if (instrumentType === 'qr' && instrumentValue) {
      this.payinCreatedQrValue = instrumentValue;
      this.payinCreatedAppLink = null;
      try {
        const QRCode = (await import('qrcode')).default;
        this.payinCreatedQrDataUrl = await QRCode.toDataURL(instrumentValue, {
          width: 240,
          margin: 2,
          errorCorrectionLevel: 'M',
        });
      } catch {
        this.payinCreatedQrDataUrl = null;
      }
      this.showPayinLinkCreatedModal = true;
      this.utilsService.copyToClipboard(instrumentValue);
      this.messageService.add({
        severity: 'success',
        summary: 'QR de pago creado',
        detail:
          'Muestra el QR al pagador o comparte el payload copiado. No es un enlace HTTP.',
        life: 8000,
      });
      return;
    }

    // Single instrument: payment_url (HTTP) or payment_instrument (QR).
    const paymentUrl =
      data?.payment_url ||
      (instrumentType === 'url' && instrumentValue
        ? instrumentValue
        : null) ||
      data?.provider_payment_url ||
      '';
    const payinId = data?.payin_id ?? data?.payinId ?? '';

    if (
      data?.depa_card &&
      this.openDepaCardWidget(paymentUrl, data?.card_fee)
    ) {
      this.messageService.add({
        severity: 'info',
        summary: 'Checkout de tarjeta listo',
        detail: 'Completa el pago en la ventana segura.',
        life: 6000,
      });
      return;
    }

    if (data?.bitso_funding && payinId && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(
        AddCashinComponent.BITSO_PENDING_PAYIN_STORAGE_KEY,
        String(payinId).trim(),
      );
    }

    if (
      paymentUrl &&
      typeof paymentUrl === 'string' &&
      paymentUrl.startsWith('http')
    ) {
      const appUrl = data?.depa_open_banking
        ? paymentUrl
        : this.isMinteoRedirectUrl(paymentUrl) && payinId
          ? `${this.PAYIN_RETURN_URL}/${encodeURIComponent(String(payinId))}`
          : paymentUrl;

      this.payinCreatedAppLink = appUrl;
      this.payinCreatedQrValue = null;
      this.payinCreatedQrDataUrl = null;
      this.showPayinLinkCreatedModal = true;
      this.messageService.add({
        severity: 'success',
        summary: data?.depa_open_banking
          ? 'Open banking iniciado'
          : data?.bitso_funding
            ? 'Link PSE creado'
            : 'Link de pago creado',
        detail: data?.depa_open_banking
          ? 'Comparte el enlace para que el pagador autorice el pago en su banco.'
          : data?.bitso_funding
            ? 'Comparte el enlace de Bitso para que el pagador complete el pago PSE.'
            : 'El enlace está listo para compartir con el pagador.',
        life: 8000,
      });
      return;
    }

    if (view.instructions || view.isManualValidation) {
      this.payinCreatedAppLink = null;
      this.payinCreatedQrValue = null;
      this.payinCreatedQrDataUrl = null;
      this.showPayinLinkCreatedModal = true;
      const copyKey =
        view.instructions?.pix_key || view.instructions?.account_number;
      if (copyKey) {
        this.utilsService.copyToClipboard(copyKey);
      }
      this.messageService.add({
        severity: view.isManualValidation ? 'warn' : 'success',
        summary: view.isManualValidation
          ? 'Cashin en validación manual'
          : 'Datos para el depósito',
        detail: view.isManualValidation
          ? view.providerError ||
            view.message ||
            'El cashin quedó en validación. No hay link ni cuenta destino.'
          : 'Copia la CLABE o cuenta destino y envía el monto exacto.',
        life: 8000,
      });
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Cashin creado',
      detail:
        (data?.message as string) ||
        'Cashin creado. Revisa el enlace de pago generado.',
      life: 6000,
    });
    this.closeAddModal();
  }

  private openDepaCardWidget(paymentUrl: unknown, cardFee?: any): boolean {
    if (typeof paymentUrl !== 'string' || !paymentUrl.startsWith('http')) {
      return false;
    }
    try {
      const parsed = new URL(paymentUrl);
      this.depaCardWidgetOrigin = parsed.origin;
      this.depaCardWidgetUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(paymentUrl);
      this.depaCardCheckoutFee = this.normalizeDepaCardFeePreview(cardFee);
      this.depaCardCheckoutCompleted = false;
      this.depaCardCheckoutStatus = 'Esperando que completes el pago.';
      this.showDepaCardCheckout = true;
      return true;
    } catch {
      return false;
    }
  }

  @HostListener('window:message', ['$event'])
  onDepaCardWidgetMessage(event: MessageEvent): void {
    if (
      !this.showDepaCardCheckout ||
      !this.depaCardWidgetOrigin ||
      event.origin !== this.depaCardWidgetOrigin
    ) {
      return;
    }
    let messageData: any = event.data;
    if (typeof messageData === 'string') {
      try {
        messageData = JSON.parse(messageData);
      } catch {
        return;
      }
    }
    const type = String(messageData?.type || '').trim();
    if (type === 'isAwaiting') {
      this.depaCardCheckoutStatus = 'Estamos procesando el pago.';
      return;
    }
    if (type === 'isFailed') {
      const payload = messageData?.payload || {};
      const refusalCode = String(payload?.refusalCode || '').trim();
      const refusalReason = String(payload?.refusalReason || '').trim();
      this.depaCardCheckoutStatus = this.getCardPaymentFailureMessage(
        refusalCode,
        refusalReason,
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Pago no completado',
        detail: this.depaCardCheckoutStatus,
      });
      return;
    }
    if (type === 'isCompleted') {
      this.depaCardCheckoutCompleted = true;
      this.depaCardWidgetUrl = null;
      this.depaCardWidgetOrigin = '';
      this.depaCardCheckoutStatus =
        'Pago recibido. El saldo se acreditará cuando finalice la confirmación.';
      this.messageService.add({
        severity: 'success',
        summary: 'Pago recibido',
        detail: this.depaCardCheckoutStatus,
        life: 8000,
      });
      this.appService.triggerTransactionCreated('cashin');
    }
  }

  private getCardPaymentFailureMessage(code: string, reason: string): string {
    if (code === '34' || reason.toUpperCase() === 'FRAUD SUSPICION') {
      return 'La operación fue rechazada por los controles de seguridad de la tarjeta. No hagas intentos repetidos; consulta al banco emisor o utiliza otra tarjeta.';
    }
    const providerDetail = [code, reason].filter(Boolean).join(': ');
    return providerDetail
      ? `No se pudo completar el pago (${providerDetail}).`
      : 'No se pudo completar el pago. Puedes cerrar y volver a intentarlo.';
  }

  closeDepaCardCheckout(): void {
    this.showDepaCardCheckout = false;
    this.depaCardWidgetUrl = null;
    this.depaCardWidgetOrigin = '';
    this.depaCardCheckoutStatus = '';
    this.depaCardCheckoutFee = null;
    this.depaCardCheckoutCompleted = false;
    this.closeAddModal();
  }

  private isMinteoRedirectUrl(url: string | null): boolean {
    if (!url) return false;
    const value = String(url).trim();
    return /minteo\./i.test(value) && /\/v1\/payins\/redirect\//i.test(value);
  }

  buildKushkiInstance = async () => {
    if (this.currency === 'PEN') {
      this.kushkiInstance = await init(this.kushkiOptions_PEN);
    }

    if (this.currency === 'USD') {
      this.kushkiInstance = await init(this.kushkiOptions_USD);
    }
  };

  buildCardInstance = async () => {
    this.kushkiCardOptions = {
      amount: {
        iva: 0,
        subtotalIva: this.amount,
        subtotalIva0: 0,
      },
      currency: this.currency as 'PEN' | 'USD',
      fields: {
        cardholderName: {
          selector: 'cardholderName_id',
        },
        cardNumber: {
          selector: 'cardNumber_id',
        },
        cvv: {
          selector: 'cvv_id',
        },
        expirationDate: {
          selector: 'expirationDate_id',
        },
      },
      styles: {
        input: {
          ['width']: '266px',
          ['border-radius']: '4px',
          color: '#3e3e3e',
          'padding-bottom': '10px',
          'padding-top': '10px',
          'padding-left': '16px',
          'padding-right': '16px',
          border: '1px solid lightgray',
        },
        cvv: {
          ['width']: '110px',
        },
        expirationDate: {
          ['width']: '110px',
        },
        invalid: {
          color: '#ef4444',
          border: '1px solid #ef4444',
        },
        focus: {
          color: '#3e3e3e !important',
        },
      },
    };

    this.cardInstance = await initCardToken(
      this.kushkiInstance!,
      this.kushkiCardOptions,
    );
  };

  createToken = async () => {
    if (this.processing) return;
    this.processing = true;

    try {
      const tokenResponse: TokenResponse =
        await this.cardInstance!.requestToken();
      // Code to send the obtained token to your back-end
      // On Success, if deferred data exist can get deferred options
      // For Ecuador, Mexico ex. {token: "a2b74b7e3cf24e368a20380f16844d16", deferred: {creditType: "03", graceMonths: 2, months: 12}}
      // For Chile, Colombia, Peru ex. {token: "a2b74b7e3cf24e368a20380f16844d16", deferred: {months: 12}}

      let token = tokenResponse.token;

      let body = {
        token: token,
        amount: {
          subtotalIva: 0,
          subtotalIva0: this.amount,
          ice: 0,
          iva: 0,
          currency: this.currency,
        },
        contactDetails: {
          documentType: this.newCashinForm.controls.documentType.value,
          documentNumber: this.newCashinForm.controls.documentNumber.value,
          email: this.newCashinForm.controls.email.value,
          firstName: this.newCashinForm.controls.firstName.value,
          lastName: this.newCashinForm.controls.lastName.value,
          phoneNumber: '+' + this.newCashinForm.controls.phone.value,
        },
        metadata: {},
        orderDetails: {
          siteDomain: 'transferswap.app',
          shippingDetails: {
            name:
              this.newCashinForm.controls.firstName.value +
              ' ' +
              this.newCashinForm.controls.lastName.value,
            phone: '+' + this.newCashinForm.controls.phone.value,
            address: this.newCashinForm.controls.address.value,
            city: this.newCashinForm.controls.city.value,
            region: this.newCashinForm.controls.region.value,
            country: this.newCashinForm.controls.country.value,
            zipCode: this.newCashinForm.controls.zipcode.value,
          },
          billingDetails: {
            name:
              this.newCashinForm.controls.firstName.value +
              ' ' +
              this.newCashinForm.controls.lastName.value,
            phone: '+' + this.newCashinForm.controls.phone.value,
            address: this.newCashinForm.controls.address.value,
            city: this.newCashinForm.controls.city.value,
            region: this.newCashinForm.controls.region.value,
            country: this.newCashinForm.controls.country.value,
            zipCode: this.newCashinForm.controls.zipcode.value,
          },
        },
        productDetails: {
          product: [
            {
              id: '1',
              title: 'Cashin TransferSwap',
              price: this.amount,
              sku: '1',
              quantity: 1,
            },
          ],
        },
        fullResponse: true,
      };

      this.cashinService.kushkiCashin(body).subscribe(
        (resp) => {
          let ps;
          if (this.currency === 'PEN') ps = 48;
          if (this.currency === 'USD') ps = 68;

          let wallet;
          if (this.currency === 'PEN') wallet = 'KUSHKI_PEN';
          if (this.currency === 'USD') wallet = 'KUSHKI_USD';

          let cashinBody = {
            reference: resp.data.details.transactionReference,
            metadata: JSON.stringify(body),
            destiny_wallet_id: wallet,
            amount: this.amount,
            provider_service_id: ps,
            client_id: this.auth.user.user_id,
            type: 'cashin',
            status: 'finished',
          };

          this.addCashin(cashinBody);
        },
        (err) => {
          this.processing = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Ocurrió un error inesperado.',
            detail: `${err.error.kushki.message}`,
          });
        },
      );
    } catch (error: any) {
      this.processing = false;
      if (error.code === 'E007') {
        this.messageService.add({
          severity: 'error',
          summary: 'Formulario inválido',
          detail:
            'Uno o más campos del formulario son inválidos, por favor revisa y vuelve a intentarlo.',
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Ocurrió un error al procesar tu tarjeta, por favor intenta nuevamente.`,
        });
      }
    }
  };

  addCashin(body: any, options?: { declared?: boolean }) {
    if (!this.auth.guardTransactionalAction()) return;
    this.transactionsService.addCashin(body).subscribe(
      () => {
        this.closeAddModal();
        this.processing = false;
        this.appService.triggerTransactionCreated('cashin');
        this.messageService.add({
          severity: 'success',
          summary: 'Listo',
          detail: options?.declared
            ? 'Hemos registrado el cashin. Quedará en validación hasta confirmar el depósito.'
            : 'Hemos recargado tu cuenta correctamente.',
        });
      },
      (err) => {
        this.processing = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Ocurrió un error al recargar tu cuenta, por favor intenta nuevamente. ${err.message}`,
        });
      },
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    if (file.name.toLowerCase().endsWith('.csv')) {
      this.selectedFile = file;
      return;
    }

    this.selectedFile = null;
    this.messageService.add({
      severity: 'error',
      summary: 'Archivo inválido',
      detail: 'Solo se permiten archivos .csv.',
    });
  }

  uploadCashinCSV(): void {
    if (!this.selectedFile || this.uploadingCSV) {
      return;
    }

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('currency', this.currency);

    this.uploadingCSV = true;
    this.transactionsService.uploadCashinCSV(formData).subscribe({
      next: (resp: any) => {
        this.uploadingCSV = false;
        const summary = resp?.data?.result;
        const created = Number(summary?.created ?? 0);
        const failed = Number(summary?.failed ?? 0);

        this.messageService.add({
          severity: failed > 0 ? 'warn' : 'success',
          summary: failed > 0 ? 'CSV procesado con errores' : 'Listo',
          detail:
            failed > 0
              ? `Cashins creados: ${created}. Filas con error: ${failed}.`
              : 'Hemos subido tu .csv correctamente.',
        });

        if (created > 0) {
          this.appService.triggerTransactionCreated('cashin');
        }

        if (failed === 0) {
          this.closeAddModal();
        }
      },
      error: (err) => {
        this.uploadingCSV = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            err?.error?.message || 'Ocurrió un error al subir tu archivo .csv.',
        });
      },
    });
  }

  closeAddModal = () => {
    this.showNewCashinType = false;
    this.showNewCashin = false;
    this.showPayinLinkCreatedModal = false;
    this.showDepaCardCheckout = false;
    this.depaCardWidgetUrl = null;
    this.depaCardWidgetOrigin = '';
    this.depaCardCheckoutStatus = '';
    this.depaCardCheckoutFee = null;
    this.depaCardCheckoutCompleted = false;
    this.payinCreatedAppLink = null;
    this.payinCreatedQrValue = null;
    this.payinCreatedQrDataUrl = null;
    this.payinCreatedDepositInstructions = null;
    this.payinCreatedManualValidation = false;
    this.payinCreatedProviderError = '';
    this.payinCreatedMessage = '';
    this.payinCreatedTransactionId = null;
    this.depaDepositInstructions = null;
    this.depaDepositLoading = false;
    this.depaDepositUnavailable = false;
    this.showDepaAccountRequest = false;
    this.selectedFile = null;
    this.uploadingCSV = false;
    this.declaredBitgoSub?.unsubscribe();
    this.declaredBitgoAddresses = [];
    this.declaredCryptoAddressLoading = false;
    this.appService.triggerAddCashin(false);
  };

  showCsvUpload(): void {
    if (!this.currencyPicker) {
      return;
    }

    this.selectedFile = null;
    this.newCashinType = 'CSV';
    this.showNewCashinType = false;
    this.showNewCashin = true;
  }

  showCashinTypeSelector(): void {
    this.selectedFile = null;
    this.uploadingCSV = false;
    this.showNewCashin = false;
    this.showNewCashinType = true;
    this.setCashinTypes();
  }

  checkContinueDisabled = () => {
    if (!this.newCashinType || !this.currencyPicker) {
      return true;
    }

    if (!this.isCashinCurrencyAllowed(this.currency)) {
      return true;
    }

    if (
      this.newCashinType !== 'CSV' &&
      this.newCashinType !== 'DEPA_IBAN' &&
      !this.amount
    ) {
      return true;
    }

    return false;
  };

  get allowedCashinCurrencies(): any[] {
    const base = this.userService.getPlatformServiceAllowedCurrencies('cashin');
    if (!this.paymentLinkOnly) {
      return base;
    }
    return base.filter((c: any) => {
      const currencyId = this.extractCurrencyId(c);
      return this.currencyHasPayinLinkProviders(currencyId);
    });
  }

  private currencyHasPayinLinkProviders(currencyId: string): boolean {
    if (!currencyId) return false;
    return this.providerServicesService.providerServices.some(
      (el: any) =>
        el.origin_coin_id === currencyId &&
        el.service_id == 'cashin' &&
        el.active &&
        this.isPayinLinkProviderService(el),
    );
  }

  private extractCurrencyId(option: any): string {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option.trim().toUpperCase();
    }

    if (typeof option === 'object') {
      const { id, currency, code } = option as {
        id?: string;
        currency?: string;
        code?: string;
      };

      const raw = id ?? currency ?? code;
      if (raw) {
        return raw.toString().trim().toUpperCase();
      }
    }

    return option.toString().trim().toUpperCase();
  }

  private isCashinCurrencyAllowed(currency: any): boolean {
    if (!this.userService.isPlatformServiceCurrencyAllowed('cashin', currency)) {
      return false;
    }
    if (!this.paymentLinkOnly) {
      return true;
    }
    return this.currencyHasPayinLinkProviders(this.extractCurrencyId(currency));
  }

  private getTransactionTypesForCurrency(currency: string): any[] {
    if (!currency) {
      return [];
    }

    const types = this.userService.user_balance_config?.transaction_types ?? [];

    return types.filter((type: any) =>
      this.transactionTypeMatchesCurrency(type, currency),
    );
  }

  private transactionTypeMatchesCurrency(
    txType: any,
    currency: string,
  ): boolean {
    if (!txType) {
      return false;
    }

    const normalizedCurrency = (currency ?? '').trim().toUpperCase();
    if (!normalizedCurrency) {
      return false;
    }

    const currencies = this.normalizeCurrencyCodes(txType.currency);

    if (!currencies.length) {
      return true;
    }

    return currencies.some(
      (code) =>
        code === normalizedCurrency ||
        code === 'ALL' ||
        code === '*' ||
        code === 'ANY',
    );
  }

  private normalizeCurrencyCodes(raw: any): string[] {
    const result = new Set<string>();
    const visited = new Set<any>();

    const visit = (value: any) => {
      if (value == null) {
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return;
        }

        if (
          (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('{') && trimmed.endsWith('}'))
        ) {
          try {
            const parsed = JSON.parse(trimmed);
            visit(parsed);
            return;
          } catch {
            // continue with the original string
          }
        }

        trimmed
          .split(/[,\|;]+/)
          .map((part) => part.trim())
          .filter(Boolean)
          .forEach((part) => result.add(part.toUpperCase()));

        return;
      }

      if (Array.isArray(value)) {
        if (visited.has(value)) {
          return;
        }
        visited.add(value);
        value.forEach(visit);
        return;
      }

      if (typeof value === 'object') {
        if (visited.has(value)) {
          return;
        }

        visited.add(value);

        if ('currency' in value) {
          visit((value as any).currency);
        }

        if ('currencies' in value) {
          visit((value as any).currencies);
        }

        if ('code' in value) {
          visit((value as any).code);
        }

        if ('codes' in value) {
          visit((value as any).codes);
        }

        return;
      }

      result.add(String(value).trim().toUpperCase());
    };

    visit(raw);

    return Array.from(result).filter(Boolean);
  }

  private isBankTransfer(value: any): boolean {
    if (!value) {
      return false;
    }

    if (typeof value === 'object') {
      if ('id' in value) {
        value = value.id;
      } else if ('name' in value) {
        value = value.name;
      } else if ('value' in value) {
        value = value.value;
      } else {
        value = value.toString();
      }
    }

    const normalized = this.normalizeText(value);
    return normalized === 'transferencia bancaria';
  }

  private normalizeText(value: any): string {
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private extractBankCode(bank: any): string | null {
    if (!bank) {
      return null;
    }

    if (typeof bank === 'string' || typeof bank === 'number') {
      const cleaned = bank.toString().trim();
      return cleaned ? cleaned.toUpperCase() : null;
    }

    if (typeof bank === 'object') {
      const primary =
        bank.id ??
        bank.code ??
        bank.value ??
        bank.bank_code ??
        bank.bankCode ??
        bank?.metadata?.code ??
        bank?.metadata?.bank_code ??
        bank?.metadata?.bankCode;

      if (primary != null && `${primary}`.trim().length) {
        return `${primary}`.trim();
      }

      const fallback =
        bank.shortname ??
        bank.name ??
        bank.label ??
        bank?.metadata?.shortname ??
        bank?.metadata?.name;

      if (fallback != null && `${fallback}`.trim().length) {
        return this.normalizeText(fallback).replace(/\s+/g, '_').toUpperCase();
      }
    }

    return null;
  }

  private extractBankName(bank: any): string {
    if (!bank) {
      return '';
    }

    if (typeof bank === 'string') {
      return bank.trim();
    }

    if (typeof bank === 'object') {
      const candidates = [
        bank.name,
        bank.shortname,
        bank.label,
        bank.title,
        bank.description,
        bank?.metadata?.name,
        bank?.metadata?.shortname,
        bank?.metadata?.label,
      ];

      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length) {
          return candidate.trim();
        }
      }
    }

    return '';
  }
}
